// ════════════════════════════════════════════════════════════
//  online/circuit.js — THE LEAGUE: the season, in rules
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-16: "make sure its a gorgeous fighting league proper with
//  no cups etc that are a waste of time, I want someone to be able to tune
//  and watch some important fights always chasing after for something
//  meaningful, real rankings real standings etc".
//  ONE SHARED WORLD ("yess that's exactly what makes this feel real and
//  canon"): every screen everywhere watches the same canon fights.
//
//  ── TWO DIVISIONS, ONE TABLE EACH ───────────────────────────
//   · THE CIRCUIT — the top 16.   · THE CONTENDERS — the 16 fighting to get in.
//   Every league bout counts on its division's table (wins – losses). The
//   table IS the ranking, #1 to #16. It resets every week; the season's
//   table is the weeks added up.
//
//  ── ONE STAGE, BACK TO BACK (stage.js runs the clock) ───────
//   One fight at a time, all day: ringside, bell, fight, ringside, bell…
//   Each hour's card is LOCKED at :50 of the hour before (caP'n, 2026-10-17: "that 10
//   minutes before the fight is absolutely crucial for betting") — the Mind picks its
//   playbooks there: neighbours on the table fight each other (Swiss). THE CIRCUIT
//   TAKES PRIORITY (caP'n, 2026-10-17: "the Contenders is more for filler just so
//   there's always a fight on"): the Circuit's bouts come first, the Contenders fill
//   the time that's left. The last bell of an hour rings by :53 —
//   whatever doesn't fit is scratched (so it's the Contenders' tail that goes).
//   WHAT THE CARD KNOWS AT :50: every row of the hours before the last one, plus the
//   last hour's fights that had ENDED by :50 (isKnown). The fight still on air then
//   is the hour's last one — who FOUGHT in it is known (so nobody gets a rematch
//   straight away), who WON it isn't (it joins the table at the next card).
//
//  ── THE DAY builds to 9pm ───────────────────────────────────
//   5–8pm   PRIME TIME — the top of the Circuit's table fights first
//   9pm     THE MAIN EVENT — the Circuit's #1 vs #2
//   Every hour's LAST CIRCUIT BOUT is its CLOSER and gets a ★ spotlight — bigger on a Friday night, in the
//   day's last hour, and when something's on the line (closerIndex / spotOf: display only, never who fights).
//
//  ── THE WEEK (Sunday's racing day starts Saturday 10pm) ─────
//   Thursday 5–8pm   THE LOSERS' CUP — the Circuit's bottom 8. Win it and
//                    you're safe from tomorrow's drop.
//   Friday 5–7pm     APOCALYPSE — the Circuit's bottom 4 (the Losers' Cup
//                    winner excused) vs the Contenders' top 4, one-on-one,
//                    best of 3: game 1 at 5, game 2 at 6, game 3 at 7 if
//                    it's 1–1. Win the series → the Circuit seat.
//   Saturday 5–9pm   THE WINNERS' CUP — the Circuit's top 8; final at 9pm.
//   Last Saturday of a season: THE SEASON FINALE takes the Winners' Cup's
//                    place — the season's top 8. Its winner is champion.
//   Cups and Apocalypse games don't touch the table (they ARE the prize).
//
//  This file is PURE (no Firebase, no clock reads): same rows in → same
//  answers on every screen. online/stage.js feeds it the ledger.
//
//  ── THE LEDGER ROW (one per finished bout; written once, first wins) ─
//   { id, kind:"lb"|"cup"|"apo", dv, fx, c, r, i, g, hk, h, j, a, b, w, t, up, an, why, by, at }
//    lb: dv "c"|"k" (the division's table), fx "main" (the Main Event)
//    cup: c "w"|"l"|"f" (Winners' / Losers' / the Finale), r round 0–2, i
//    apo: i matchup 0–3 (0 = the very bottom vs the best Contender), g game 0–2
//    a/b = keys, w = 0|1 winner, t = fight seconds, up = beings standing,
//    an = Anima left, hk = the hour's kind, h = hour, j = place on the card, at = the bell (ms)
// ════════════════════════════════════════════════════════════
import { CAST_TEAMS, teamFromNames } from "./rooms.js";
import CONT from "../data/contendersCast.json";
import { TEAM_SIZE } from "../sim/battle.js";
import { pointsFold, paced, SALVATION_LINE, SALVATION_MIN, STANDOUT_LINE, CROWN_ENTRY_TIER } from "./points.js";

// ── DIALS ────────────────────────────────────────────────────
export const CIRCUIT_TZ = "America/Los_Angeles";   // ⟵ DIAL — caP'n's clock ("yes, my time zone")
// ⚠ only zones on whole-hour offsets: hours are cut on UTC hour lines (a :30 zone would shift every bell)
export const WORLD = "w";                          // the shared world's server id (rooms still use SERVERS)
// Season 1 begins Saturday 2026-10-17, 10pm Pacific (the first racing week of the League as a league).
// Before it: PRESEASON — the stage runs, nothing counts.
export const GENESIS_MS = Date.UTC(2026, 9, 18, 5, 0, 0);
export const LEAGUE_SIZE = 16;                     // each division
export const CUP_SIZE = 8;                         // ⟵ DIAL — the Winners' / Losers' Cup / the Finale
export const DROP = 4;                             // ⟵ DIAL — Apocalypse: the bottom 4 vs the top 4
export const SERIES_WINS = 2;                      // Apocalypse: best of 3
export const CALL = 300;                           // s — every bout is the game's 5:00
export const RING = 85;                            // ⟵ DIAL s — ringside before a league bout (bets close at the bell) (2026-10-21: was 90 — caP'n: "don't be scared to adjust the timers a bit if it means more fights"; with the 2:30 big ringside the new evenings run ~1% MORE fights than the old ones)
export const RING_BIG = 150;                       // ⟵ DIAL s — before a cup final, Saturday cup, Apocalypse or Main Event bout (caP'n 2026-10-21: "drop it to 2:30 … don't be scared to adjust the timers a bit if it means more fights"; was 3:00)
export const EST_FIGHT_S = 195;                   // ⟵ DIAL s — how long a fight is GUESSED to last, for the "about" bell times shown before a bell is known (measured avg ~195 s)
export const RING_OPEN = 120;                      // ⟵ DIAL s — the hour's first ringside (the card was just locked)
export const LAST_BELL_MIN = 53;                   // ⟵ DIAL — no bell after :53 (the bout is over by :58)
export const CARD_MAX = 16;                        // a card holds everyone once (8 + 8); the tail is scratched
export const PAPER_BOUTS = 11;                     // ⟵ DIAL — bouts an hour fights when it's decided on paper
export const REMATCH_GAP = 3;                      // ⟵ DIAL — the table pairs you with someone you haven't met in your last 3
export const LOCK_GRACE_MS = 3000;                 // a lock waits this long past the hour (stage.js) — only when it couldn't go early
export const LOCK_LEAD_MS = 10 * 60e3;             // ⟵ DIAL — the card goes up this long BEFORE its hour (bets open then)
export const DAY_TURN_HOUR = 22;                   // a racing day runs 10pm → 9:59pm (until V2_MS)
// ── THE NEW EVENINGS (caP'n 2026-10-21) ─────────────────────
// "maybe the Hourly is 1v1's no problem but then 5PM starts the actual tournament of contenders until a true
// champion is crowned, then the circuit tournament starts" · "Let's move Prime Time up to 6PM so the final fight
// is at 10PM that always includes the good cups, finals, etc". Everything before V2_MS keeps the rules it was
// fought under (week 1 finishes the way it started); from V2_MS on, hourInfo/cups/Apocalypse read the new evenings.
export const V2_MS = Date.UTC(2026, 9, 25, 6, 0, 0); // ⟵ DIAL — Sat 2026-10-24, 11pm Pacific: the start of Season 1, week 2
export const DAY_TURN_V2 = 23;                     // from V2_MS a racing day runs 11pm → 10:59pm, so the 10pm finals stay in their own day (and Saturday's in its own week)
/** The local hour a racing day turns over at, for an instant. */
export const dayTurnOf = (ms) => (ms < V2_MS ? DAY_TURN_HOUR : DAY_TURN_V2);
// ── THE CUP NIGHTS + TWO STAGES (caP'n 2026-10-21/22) — "V3" ─
// "The Contenders Tournament like the Circuit should be 24/7 … they run in unison" · five cups every night, "arrange them from
// least epic to most epic", the Circuit Cup's final last · Last Chance and Top vs Top each let one Contender escape after the
// night's cups · Friday stays Apocalypse · Saturday is SALVATION ("just another name for King's Cup") on points. From V3_MS on,
// hourInfo/cups/the card read this; everything before keeps the rules it was fought under.
export const V3_MS = V2_MS;                        // ⟵ DIAL — caP'n 2026-10-22: "week 2 <3" — Sat 2026-10-24, 11pm Pacific (was Oct 31; V2's evenings never run)
export const LEGACY_BESTLAST_MS = Date.UTC(2026, 9, 23, 19, 0, 0); // ⟵ DIAL — Fri 2026-10-23, noon Pacific: from here the REST OF WEEK 1's plain hours (league / Prime Time / the Main Event) also run best-last, #1's bout closing the hour (caP'n 2026-10-22: "we need the best fights last … #1 always fights last"). Hours before it keep the order they were fought under; V3 (week 2) already has it. Set it LATER than the deploy.
export const CALL_K = 240;                         // ⟵ DIAL s — a Contenders-stage league bout is called at 4:00 (caP'n 2026-10-16, Kings Road: "fights capped at 4:00 so one is guaranteed to start every 5 minutes")
export const RING_K = 60;                          // ⟵ DIAL s — ringside before a Contenders-stage league bout ("no intro"): 4:00 + 1:00 = a fight at least every 5 minutes
export const LANES = ["The Circuit stage", "The Contenders stage"];
export const AIR_BOUTS = 10;                         // ⟵ DIAL — bouts per stage-hour the clock realistically airs (FIGHTS_PER_HOUR−1); the filler tail after it is the scratchable overflow, kept out of the weak→strong sort
export const CH_GAP = 3;                           // ⟵ DIAL — a Challenger's two bouts in one hour sit at least this many bouts apart   // ⟵ DIAL — lane 0 / lane 1 (a bout's `ln`)
export const SPECIAL_NIGHTS = { 4: "Thursday night", 5: "Friday night", 6: "Saturday night" }; // ⟵ DIAL — calendar weekday (0 = Sunday) → an evening that gets its own name on the closing bout (the cup nights; caP'n 2026-10-17: yes to more than Friday)
export const NIGHT_FROM = 17;                      // ⟵ DIAL — the local hour an evening starts
export const FIGHTS_PER_HOUR = Math.floor((LAST_BELL_MIN * 60 - RING_OPEN) / (RING + EST_FIGHT_S)) + 1;   // ≈ 11 — only for "about N fights left" (never a promise)

export const HOUR_MS = 3600e3;
const MIN = 60e3;
export const KINDS = {
  league: { name: "The League", short: "League" },
  preseason: { name: "Preseason", short: "Preseason" },
  prime: { name: "Prime Time", short: "Prime Time" },
  main: { name: "The Main Event", short: "Main Event" },
  losers: { name: "The Losers' Cup", short: "Losers' Cup" },
  winners: { name: "The Winners' Cup", short: "Winners' Cup" },
  finale: { name: "The Season Finale", short: "The Finale" },
  apocalypse: { name: "Apocalypse", short: "Apocalypse" },
  kcup: { name: "The Contenders Cup", short: "Contenders Cup" },     // V2: Sun–Thu, 6pm → the final at 9pm (an event name; its hours are "ccup")
  ccup: { name: "The Circuit Cup", short: "Circuit Cup" },           // V2: Sun–Thu, 6pm → the final at 10pm
  champions: { name: "The Champions Cup", short: "Champions Cup" },  // V2: Saturday, 8pm → the final at 10pm
  // V3 (caP'n 2026-10-22) — the cup nights: the afternoon's early rounds, then one final an hour, least epic to most epic
  rounds: { name: "The Cup Rounds", short: "Cup Rounds" },          // Sun–Thu 1pm → 5pm
  bbcup: { name: "Bottom vs Bottom", short: "Bottom vs Bottom" },   // 6pm — its final
  kccup: { name: "The Contenders Cup", short: "Contenders Cup" },   // 7pm
  lccup: { name: "Last Chance", short: "Last Chance" },             // 8pm — one escape
  ttcup: { name: "Top vs Top", short: "Top vs Top" },               // 9pm — one escape (10pm is "ccup": the Circuit Cup final)
  salvation: { name: "Salvation", short: "Salvation" },             // Saturday 8pm → 10pm — the King's Cup
  nccup: { name: "The Night Crown", short: "Night Crown" },         // Sun–Thu 10pm — one fight after the Circuit Cup final (an event name; its hour is "ccup")
  qual: { name: "The Qualifiers", short: "Qualifiers" },            // Sun–Thu 11am → 1pm — four baby tourneys of 8 (7 fights each, back to back) that order the night's cups
};
export const CUPS = {
  w: { name: "the Winners' Cup", kind: "winners", size: 8 },              // before V2 only
  l: { name: "the Losers' Cup", kind: "losers", size: 8 },                // before V2: Thursday, saves you from the drop · V2: Saturday, bragging rights only
  f: { name: "the Season Finale", kind: "finale", size: 8 },
  k: { name: "the Contenders Cup", kind: "kcup", size: 8, daily: true },   // V2 — the Contenders' top 8 (caP'n 2026-10-21: both cups inside Prime Time)
  c: { name: "the Circuit Cup", kind: "ccup", size: 16, daily: true },     // V2
  x: { name: "the Champions Cup", kind: "champions", size: 8 },           // V2
  // V3 — the five cups of a night (tier = least → most epic: what a win in it is worth, points.js) + Saturday's Salvation.
  // ⟵ DIAL: the names. Halves are drawn off each division's table at the night's cut (1pm card): top 8 / next 8.
  bb: { name: "Bottom vs Bottom", kind: "bbcup", size: 16, daily: true, tier: 1, sides: ["cb", "kb"] },        // no escape
  kc: { name: "the Contenders Cup", kind: "kccup", size: 16, daily: true, tier: 2, sides: ["k"] },             // all 16 (caP'n: "yes")
  lc: { name: "Last Chance", kind: "lccup", size: 16, daily: true, tier: 3, sides: ["cb", "kt"], escape: true },
  tt: { name: "Top vs Top", kind: "ttcup", size: 16, daily: true, tier: 4, sides: ["ct", "kt"], escape: true },
  s: { name: "Salvation", kind: "salvation", size: 16, tier: 6, points: true },                                // Saturday — the King's Cup, on points
};
CUPS.c.tier = 5; CUPS.c.sides = ["c"];
// THE NIGHT CROWN (caP'n 2026-10-22: "night crown sounds like a kings crown"): ONE fight at 10pm, drawn off the night's four earlier
// champions (by the night's points); one who is also in the Circuit Cup final fights that first, then this. size 2 = a bracket of one bout. See cupFieldV3.
CUPS.nc = { name: "the Night Crown", kind: "nccup", size: 2, daily: true, tier: 7 };
// THE QUALIFIERS (caP'n 2026-10-23: "just add more tournaments … winning these baby tourneys during prime time is how you qualify for the real prime
// time"): four tourneys of 8 every cup night, 7 fights each, ROLLING back to back inside their hour (a round's matchup is read off the fight before it — a
// "late-bound" bout, see isBound). Each division's top 16 is dealt into two pools of 8 (a snake: 1·4·5·8·9·12·13·16 / 2·3·6·7·10·11·14·15); the Circuit's
// play on the Circuit stage, the Contenders' on theirs, 11am and noon. How far you got orders the night: a Qualifier's winner and finalist and its two
// semifinal losers (the semifinalists) fill their division's TOP half — the seats of Top vs Top — and the four who lost in the quarters the bottom half.
// tier 0 / no crown: a qualifier pays its fights' ordinary points and nothing more.
CUPS.q1 = { name: "Circuit Qualifier I", kind: "qual", size: 8, daily: true, q: true, dv: "c", pool: 0, hour: 0 };
CUPS.q2 = { name: "Contenders Qualifier I", kind: "qual", size: 8, daily: true, q: true, dv: "k", pool: 0, hour: 0 };
CUPS.q3 = { name: "Circuit Qualifier II", kind: "qual", size: 8, daily: true, q: true, dv: "c", pool: 1, hour: 1 };
CUPS.q4 = { name: "Contenders Qualifier II", kind: "qual", size: 8, daily: true, q: true, dv: "k", pool: 1, hour: 1 };
/** The night's four Qualifiers, in the order they ring (11am: Circuit stage, Contenders stage · noon: the same). */
export const QUALS = ["q1", "q2", "q3", "q4"];
export const isQual = (c) => !!(CUPS[c] && CUPS[c].q);
export const BREATHER_S = 240;                     // ⟵ DIAL s — the downtime after a tournament's final before its stage's next fight (caP'n 2026-10-23: "maybe 3-5 minutes")
export const QUAL_POOL_A = [0, 3, 4, 7, 8, 11, 12, 15], QUAL_POOL_B = [1, 2, 5, 6, 9, 10, 13, 14];   // ⟵ DIAL — a division's top 16 dealt into two pools of 8 (snake)
/** The night's cups, least epic first (V3): their finals ring 6, 7, 8, 9 and 10pm. */
export const NIGHT_CUPS = ["bb", "kc", "lc", "tt", "c"];
/** The night's five cups + the Night Crown (V3): what a cup night holds. NIGHT_CUPS stays the five that are drawn off the 1pm table and share one check-in. */
export const NIGHT_ALL = [...NIGHT_CUPS, "nc"];
/** A cup's rounds (8 → 3, 16 → 4) and its final's round index. */
export const roundsOf = (c) => (CUPS[c] && CUPS[c].size === 16 ? 4 : CUPS[c] && CUPS[c].size === 2 ? 1 : 3);
export const finalR = (c) => roundsOf(c) - 1;

// ── WHO EXISTS ───────────────────────────────────────────────
// Circuit cast = caP'n's Hourly list (keys npc1…); Contenders = his second list (c1…)
export const CIRCUIT_CAST = CAST_TEAMS.slice().sort((a, b) => a.n - b.n).slice(0, LEAGUE_SIZE)
  .map((e) => ({ key: `npc${e.n}`, n: e.n, name: e.name, team: e.team, home: "circuit" }));
export const CONTENDER_CAST = (CONT.entrants || []).map((e) => ({ key: `c${e.n}`, n: e.n, name: e.name, team: teamFromNames(e.Beings), home: "contenders" }))
  .filter((e) => e.team.length === TEAM_SIZE).sort((a, b) => a.n - b.n).slice(0, LEAGUE_SIZE);
export const BEINGS = new Map([...CIRCUIT_CAST, ...CONTENDER_CAST].map((e) => [e.key, e]));
const castOrder = (k) => { const e = BEINGS.get(k); return e ? (e.home === "circuit" ? e.n : 100 + e.n) : 1000; };
export const GENESIS_LEAGUE = { circuit: CIRCUIT_CAST.map((e) => e.key), contenders: CONTENDER_CAST.map((e) => e.key) };

// ── THE CLOCK (caP'n's time zone, worked out the same on every screen) ─
const fmt = typeof Intl !== "undefined" ? new Intl.DateTimeFormat("en-US", {
  timeZone: CIRCUIT_TZ, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", weekday: "short",
}) : null;
const DOW = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const partsCache = new Map();
function localParts(ms) {
  const key = Math.floor(ms / MIN);
  let p = partsCache.get(key);
  if (!p) {
    const o = {};
    for (const x of fmt.formatToParts(new Date(key * MIN))) o[x.type] = x.value;
    p = { y: +o.year, m: +o.month, d: +o.day, hour: +o.hour % 24, minute: +o.minute, dow: DOW[o.weekday] };
    if (partsCache.size > 5000) partsCache.clear();
    partsCache.set(key, p);
  }
  return p;
}
const dayIndexOf = (p) => Math.round(Date.UTC(p.y, p.m - 1, p.d) / 864e5);
/** The racing day an instant belongs to (10pm starts the next one). */
export function racingDayOf(ms) {
  const p = localParts(ms);
  let di = dayIndexOf(p);
  if (p.hour >= dayTurnOf(ms)) di += 1;
  return di;
}
export const dowOfDay = (di) => ((di % 7) + 4 + 7) % 7;          // 1970-01-01 was a Thursday
export const weekOfDay = (di) => Math.floor((di + 4) / 7);         // weeks start on Sunday's racing day
export const weekOf = (ms) => weekOfDay(racingDayOf(ms));
export const hourOf = (ms) => Math.floor(ms / HOUR_MS);
export const hourStart = (h) => h * HOUR_MS;
/** When hour h's card goes up (and bets open): LOCK_LEAD_MS before the hour. */
export const lockAtOf = (h) => hourStart(h) - LOCK_LEAD_MS;
/** When a ledger row's fight ended (a paper row has no length: it ends at its bell). */
const rowEnd = (r) => r.at + (Number.isFinite(r.t) ? r.t : 0) * 1000;
/**
 * Does hour h's card know this row? Every row of the hours before the last one, plus the last
 * hour's rows that had ended by the time h's card goes up. A pure function of the row itself, so
 * every screen draws the same line however late the lock is actually written.
 */
export const isKnown = (r, h) => r.h < h - 1 || (r.h === h - 1 && rowEnd(r) <= lockAtOf(h));
export const knownRows = (rows, h) => rows.filter((r) => isKnown(r, h));

// ════════════════════════════════════════════════════════════
//  SEASONS — four weeks (caP'n: "whatever's easier partner never convolute … 4xS")
// ════════════════════════════════════════════════════════════
export const SEASON_WEEKS = 4;                                       // ⟵ DIAL
const WEEK0 = weekOf(GENESIS_MS);
export const seasonOf = (w) => Math.floor((w - WEEK0) / SEASON_WEEKS);   // 0 = the first season
export const seasonWeeks = (s) => Array.from({ length: SEASON_WEEKS }, (_, i) => WEEK0 + s * SEASON_WEEKS + i);
export const weekOfSeason = (w) => ((w - WEEK0) % SEASON_WEEKS) + 1;     // 1…SEASON_WEEKS

/** Everything about one hour of the schedule. Pure. */
export function hourInfo(h) {
  const ms = hourStart(h);
  const p = localParts(ms);
  const day = racingDayOf(ms), week = weekOfDay(day), lastWeek = weekOfSeason(week) === SEASON_WEEKS;
  const sat = lastWeek ? "finale" : "winners";
  const v2 = ms >= V2_MS, v3 = ms >= V3_MS;   // V3 keeps V2's evenings frame (Prime Time 6–10, no Main Event, the 11pm day turn)
  let kind = "league";
  if (v3) kind = v3Kind(p, lastWeek);
  else if (v2) kind = v2Kind(p, lastWeek);
  else if (p.hour >= 17 && p.hour <= 19) kind = p.dow === 4 ? "losers" : p.dow === 5 ? "apocalypse" : p.dow === 6 ? sat : "prime";
  else if (p.hour === 20) kind = p.dow === 4 ? "losers" : "prime";
  else if (p.hour === 21) kind = p.dow === 6 ? sat : "main";
  if (ms < GENESIS_MS) kind = "preseason";   // before Season 1: bouts for show, nothing counts, no cups
  // PRIME TIME: the top of the Circuit's table fights first (V2: 6pm → 10pm, every night)
  const prime = v2 ? kind !== "preseason" && p.hour >= PRIME_FROM && p.hour <= PRIME_TO : kind === "prime" || kind === "main";
  return { h, ms, localHour: p.hour, dow: p.dow, day, week, kind, counts: ms >= GENESIS_MS, v2, v3, prime, ...KINDS[kind] };
}
/**
 * THE CUP NIGHTS, by the clock (V3). Sun–Thu: the cup rounds 1pm → 5pm, then one final an hour — Bottom vs Bottom 6pm,
 * the Contenders Cup 7pm, Last Chance 8pm, Top vs Top 9pm, the Circuit Cup 10pm. Friday: Prime Time, then Apocalypse at
 * 8, 9 and 10pm. Saturday: the Losers' Cup 6pm → 8pm, Salvation 8pm → 10pm (the Season Finale instead on a season's last).
 */
const V3_NIGHT = { 11: "qual", 12: "qual", 13: "rounds", 14: "rounds", 15: "rounds", 16: "rounds", 17: "rounds", 18: "bbcup", 19: "kccup", 20: "lccup", 21: "ttcup", 22: "ccup" };
function v3Kind(p, lastWeek) {
  const hr = p.hour, d = p.dow;
  if (d >= 5 && (hr === 11 || hr === 12)) return "qual";   // (caP'n 2026-10-23: one system, every day — Friday's and Saturday's mornings are Qualifiers too)
  if (d === 5) return hr >= 20 && hr <= 22 ? "apocalypse" : "league";
  if (d === 6) {
    if (lastWeek) return hr === 14 || hr === 16 || hr === 22 ? "finale" : hr >= 18 && hr <= 19 ? "losers" : hr >= 20 && hr <= 21 ? "salvation" : "league";
    return hr >= 18 && hr <= 19 ? "losers" : hr >= 20 && hr <= 22 ? "salvation" : "league";
  }
  return V3_NIGHT[hr] || "league";
}
export const PRIME_FROM = 18, PRIME_TO = 22;       // ⟵ DIALS (V2) — Prime Time, 6pm → the 10pm finals
/**
 * THE NEW EVENINGS, by the clock (V2). Sun–Thu: the Circuit Cup 6pm → 10pm and the Contenders Cup (top 8) 6pm → 9pm.
 * Friday: Prime Time, then Apocalypse at 8, 9 and 10pm. Saturday: the Losers' Cup 6pm → 8pm, the Champions
 * Cup 8pm → 10pm (the Season Finale instead on a season's last Saturday). An hour's kind is its NAME; which
 * cup bouts it holds is V2_SLOTS (an hour can hold bouts of two cups — 6–8pm Sun–Thu, 8pm Saturday).
 */
function v2Kind(p, lastWeek) {
  const hr = p.hour, d = p.dow;
  if (d === 5) return hr >= 20 && hr <= 22 ? "apocalypse" : "league";
  if (d === 6) return hr >= 18 && hr <= 19 ? "losers" : hr >= 20 && hr <= 22 ? (lastWeek ? "finale" : "champions") : "league";
  return hr >= 18 && hr <= 22 ? "ccup" : "league";   // both nightly cups live inside Prime Time (6pm → 10pm)
}
/** The hours of a racing day, in order. */
export function hoursOfDay(di) {
  const out = [];
  let h = hourOf(Date.UTC(1970, 0, 1) + (di - 1) * 864e5) - 14;   // well before it, in any time zone
  while (racingDayOf(hourStart(h)) < di) h++;
  while (racingDayOf(hourStart(h)) === di) { out.push(h); h++; }
  return out;
}
export const hoursOfWeek = (w) => { const out = []; for (let d = w * 7 - 4; d < w * 7 + 3; d++) out.push(...hoursOfDay(d)); return out; };
const weekKindCache = new Map();
/** A week's hours of one kind, in order (the cup's / the Apocalypse's evening). */
export function kindHours(w, kind) {
  const k = `${w}:${kind}`;
  let v = weekKindCache.get(k);
  if (!v) { v = hoursOfWeek(w).filter((h) => hourInfo(h).kind === kind); weekKindCache.set(k, v); if (weekKindCache.size > 400) weekKindCache.clear(); }
  return v;
}

// ── SEEDED SHUFFLES ──────────────────────────────────────────
export function mulberry(a) {
  return () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
export const strSeed = (s) => { let h = 2166136261; for (const c of String(s)) h = Math.imul(h ^ c.charCodeAt(0), 16777619); return h >>> 0; };

// ════════════════════════════════════════════════════════════
//  THE RECORD — a fold over ledger rows
// ════════════════════════════════════════════════════════════
export const ELO_START = 1500, ELO_K = 24;   // ⟵ DIALS — the rating (Willie's odds); every bout moves it
const blank = (key) => ({
  key, played: 0, won: 0, lost: 0, lw: 0, ll: 0, cw: 0, cl: 0, kw: 0, kl: 0,
  winT: 0, timed: 0, wins: 0, anima: 0, form: [], streak: 0, last: 0, elo: ELO_START, cups: { w: 0, l: 0, f: 0, k: 0, c: 0, x: 0, bb: 0, kc: 0, lc: 0, tt: 0, s: 0, nc: 0, q1: 0, q2: 0, q3: 0, q4: 0 }, mains: 0,
});
export const eloP = (ra, rb) => 1 / (1 + 10 ** ((rb - ra) / 400));
const KINDS_OK = { lb: (r) => r.dv === "c" || r.dv === "k", cup: (r) => !!CUPS[r.c] && Number.isInteger(r.r) && r.r >= 0 && r.r <= finalR(r.c) && Number.isInteger(r.i) && r.i >= 0 && r.i < (CUPS[r.c].size >> (r.r + 1)), apo: (r) => r.i >= 0 && r.i < DROP && (r.g === 0 || r.g === 1 || r.g === 2) };
/**
 * Only well-formed rows count (rows are written by screens, so a broken one must never
 * take down every board): a winner, two different keys, a real bell after genesis, a known kind.
 */
export function validRow(r) {
  return !!r && (r.w === 0 || r.w === 1) && typeof r.a === "string" && typeof r.b === "string" && r.a !== r.b
    && Number.isFinite(r.at) && r.at >= GENESIS_MS && Number.isInteger(r.h) && typeof r.id === "string" && !!KINDS_OK[r.kind] && KINDS_OK[r.kind](r);
}
/** Valid rows, one per id (the first written wins), in bell order. */
export function cleanRows(rows) {
  const seen = new Set(), out = [];
  for (const r of rows) if (validRow(r) && !seen.has(r.id)) { seen.add(r.id); out.push(r); }
  return out.sort((x, y) => x.at - y.at || (x.id < y.id ? -1 : x.id > y.id ? 1 : 0));
}
/** A cup's winner from its final row (null if not fought). */
const cupChamp = (row) => (row.kind === "cup" && row.r === finalR(row.c) ? (row.w === 0 ? row.a : row.b) : null);
/**
 * rows → Map(key → stats). Rows are applied in bell order.
 * onRow(row, { pa, pre:[a,b] }, S) — called after each row: pa = A's chance going in, pre = streak going in.
 * stats: played won lost (every bout) · lw ll (league bouts = the table) · cw cl / kw kl (league bouts in
 *        the Circuit / the Contenders — the season tables) · winT/timed (avg win time) wins anima (Anima left,
 *        summed over wins) · form (W/L, newest last, ≤10) · streak (+W / −L) · last (ms) · elo
 *        cups { w, l, f, k, c, x } (cups won) · mains (Main Events won)
 */
export function standings(rows, eloStart = null, onRow = null) {
  const S = new Map();
  S.eloStart = eloStart;   // a being with no fight yet this week still carries last week's rating
  const get = (k) => { let s = S.get(k); if (!s) { s = blank(k); if (eloStart && eloStart[k] != null) s.elo = eloStart[k]; S.set(k, s); } return s; };
  for (const row of cleanRows(rows)) {
    const ea = get(row.a), eb = get(row.b), pa = eloP(ea.elo, eb.elo), d = ELO_K * ((row.w === 0 ? 1 : 0) - pa);
    const pre = onRow && [ea, eb].map((x) => ({ streak: x.streak }));   // for the Wire: how things stood going in
    ea.elo += d; eb.elo -= d;
    [row.a, row.b].forEach((k, side) => {
      const s = get(k), won = row.w === side;
      s.played++; s[won ? "won" : "lost"]++;
      if (row.kind === "lb") {
        s[won ? "lw" : "ll"]++;
        if (row.dv === "c") s[won ? "cw" : "cl"]++; else s[won ? "kw" : "kl"]++;
        if (won && row.fx === "main") s.mains++;
      }
      if (won) {
        s.wins++; s.anima += (row.an && Number.isFinite(row.an[side]) ? row.an[side] : 0);
        if (Number.isFinite(row.t) && row.t > 0) { s.winT += row.t; s.timed++; }   // a paper win (no time) never counts as "fast"
      }
      s.form.push(won ? "W" : "L"); if (s.form.length > 10) s.form.shift();
      s.streak = won ? (s.streak > 0 ? s.streak + 1 : 1) : (s.streak < 0 ? s.streak - 1 : -1);
      s.last = row.at;
    });
    const champ = cupChamp(row);
    if (champ) get(champ).cups[row.c]++;
    if (onRow) onRow(row, { pa, pre }, S);
  }
  return S;
}
export const statOf = (S, k) => S.get(k) || { ...blank(k), elo: (S.eloStart && S.eloStart[k] != null) ? S.eloStart[k] : ELO_START };
export const avgWin = (s) => (s.timed ? s.winT / s.timed : 1e9);   // no timed wins: slowest (a number, so the sort never sees NaN)
// which record a table reads: the week's table (lw/ll), or a season table per division
export const REC = { week: (s) => [s.lw, s.ll], circuit: (s) => [s.cw, s.cl], contenders: (s) => [s.kw, s.kl] };
// ════════════════════════════════════════════════════════════
//  GUESTS — players (and partners) take a Circuit seat (caP'n 2026-10-20)
// ════════════════════════════════════════════════════════════
// "There needs to be a way to join the Circuit without taking over another body. What if in the 10
// minutes prior to the tourney, anyone can join and replace the lowest fighters" — every hour (a),
// as GUESTS (b: "guests <3" — a guest's bouts never touch the table, the ratings or the minds), up to
// 4 seats (c), anyone for now (d). The seats are fixed WITH THE LOCK (:50): the bottom of the Circuit's
// table, each in their first league bout of the hour. A seat nobody takes stays the NPC's.
export const GUEST_SEATS = 4;                       // ⟵ DIAL — player seats an hour
export const GUEST_PURSE = 1;                       // ⟵ DIAL — King's Krests for a guest's win
export const guestKey = (uid) => `g_${uid}`;
export const isGuestKey = (k) => typeof k === "string" && k.startsWith("g_");
/** The hour's open seats: [{ key (the NPC it replaces), j (the bout), side }], lowest of the table first. */
export function guestSeats(card, league, S, n = GUEST_SEATS) {
  const seats = [], taken = new Set();
  for (const k of rankKeys((league && league.circuit) || [], S).reverse()) {
    if (seats.length >= n) break;
    const j = (card || []).findIndex((b) => b && b.k === "lb" && (b.a === k || b.b === k));
    if (j < 0) continue;
    const side = card[j].a === k ? 0 : 1;
    if (taken.has(`${j}:${side}`)) continue;
    taken.add(`${j}:${side}`);
    seats.push({ key: k, j, side });
  }
  return seats;
}

/**
 * Best first. THE TABLE (caP'n 2026-10-21: "yes way better" — win %, so a player who can't fight 24 times a day isn't
 * buried by it): QUALIFIED first (at least QUALIFY bouts on this table), then win % → wins minus losses → more wins →
 * faster wins → more Anima left (per win) → caP'n's list order. The NPCs fight about the same amount, so among them it
 * reads almost exactly like the old wins-minus-losses table; it's the Challengers it's fair to.
 */
export const QUALIFY = 5;                          // ⟵ DIAL — bouts on a table before you're ranked among the qualified (and can be seeded into a cup)
export function rankKeys(keys, S, rec = REC.week) {
  const info = new Map();
  for (const k of new Set(keys)) { const s = statOf(S, k), [w, l] = rec(s); info.set(k, { s, w, l, q: w + l >= QUALIFY ? 1 : 0, p: w + l ? w / (w + l) : 0 }); }
  return [...info.keys()].sort((x, y) => {
    const A = info.get(x), B = info.get(y), a = A.s, b = B.s;
    return B.q - A.q || B.p - A.p || (B.w - B.l) - (A.w - A.l) || B.w - A.w || avgWin(a) - avgWin(b) || (b.anima / (b.wins || 1)) - (a.anima / (a.wins || 1)) || castOrder(x) - castOrder(y) || (x < y ? -1 : x > y ? 1 : 0);
  });
}
// ════════════════════════════════════════════════════════════
//  CHALLENGERS — players and partners, official League members (caP'n 2026-10-21; online/challengers.js)
// ════════════════════════════════════════════════════════════
/** Every Challenger any screen has read: key → { key, uid, name, … } (names for the boards; the rules never read it). */
export const CHALLENGERS = new Map();
export const chKey = (uid) => `ch_${uid}`;
export const isChKey = (k) => typeof k === "string" && k.startsWith("ch_");
export const uidOfKey = (k) => (isChKey(k) ? k.slice(3) : null);
/** A name for any key: an NPC's, a Challenger's, or a stand-in. */
export const nameOfKey = (k) => (BEINGS.get(k) || CHALLENGERS.get(k) || {}).name || (isChKey(k) ? "A Challenger" : k);

// ════════════════════════════════════════════════════════════
//  THE CUPS — every bout of them a feature
//   before V2: two a week (Thursday's Losers' Cup, Saturday's Winners' Cup) + the Finale
//   V2 (caP'n 2026-10-21): every night Sun–Thu a CIRCUIT CUP (16, 6pm → 10pm) and a CONTENDERS CUP (top 8, 6pm → 9pm),
//   one bracket each, "until a true champion is crowned". Saturday: THE LOSERS' CUP
//   (6pm → 8pm, bragging rights — "nothing to do with promotion … another awesome medal") and THE CHAMPIONS
//   CUP (8pm → 10pm: the week's cup winners). The Season Finale takes the Champions Cup's place on a
//   season's last Saturday. Cup bouts never touch the table — the cup IS the prize.
// ════════════════════════════════════════════════════════════
// Where each cup's bouts sit (before V2): by the index of the cup's hour that evening.
// 1st hour: QF1 QF2 · 2nd: QF3 QF4 · 3rd: SF1 SF2 · 4th: the Final.
const CUP_SLOTS = [[[0, 0], [0, 1]], [[0, 2], [0, 3]], [[1, 0], [1, 1]], [[2, 0]]];
const SEED_ORDER = [0, 7, 3, 4, 1, 6, 2, 5];   // 1v8, 4v5, 2v7, 3v6 — the top two can only meet in the final
// 1v16, 8v9, 4v13, 5v12 | 2v15, 7v10, 3v14, 6v11 — the top four can only meet in the semis, the top two in the final
const SEED16 = [0, 15, 7, 8, 3, 12, 4, 11, 1, 14, 6, 9, 2, 13, 5, 10];
const CUP_OF_KIND = { winners: "w", losers: "l", finale: "f" };
const slot = (r, from, to) => Array.from({ length: to - from + 1 }, (_, n) => [r, from + n]);
/**
 * V2: which cup bouts each LOCAL hour holds — { cup: { localHour: [[round, index], …] } }.
 * THE :50 LAW shapes it: an hour's card goes up at :50 of the hour before, so a round can only be drawn once
 * every bout feeding it has ENDED by then — a round never shares an hour with its feeders, and a bout whose
 * winner is needed the next hour sits in the first 6 of its hour (worst case, every fight to the 5:00 bell:
 * 2:00 + 5 × (3:00 ringside + 5:00) + 5:00 = 47 min, before :50). Never more than 6 cup bouts an hour.
 *   Sun–Thu (caP'n 2026-10-21: both inside Prime Time — the Circuit's 16, the Contenders' top 8)
 *            6pm C R16 1–2, K QF 1–4 · 7pm C R16 3–8 · 8pm C QF 1–4, K SF 1–2 · 9pm C SF 1–2, K FINAL · 10pm C FINAL
 *   Saturday 6pm L QF · 7pm L SF · 8pm X QF, L FINAL · 9pm X SF · 10pm X FINAL (the Finale uses X's hours)
 */
export const V2_SLOTS = {
  c: { 18: slot(0, 0, 1), 19: slot(0, 2, 7), 20: slot(1, 0, 3), 21: slot(2, 0, 1), 22: [[3, 0]] },
  k: { 18: slot(0, 0, 3), 20: slot(1, 0, 1), 21: [[2, 0]] },
  x: { 20: slot(0, 0, 3), 21: slot(1, 0, 1), 22: [[2, 0]] },
  l: { 18: slot(0, 0, 3), 19: slot(1, 0, 1), 20: [[2, 0]] },
};
V2_SLOTS.f = V2_SLOTS.x;
const dayIsV2 = (day) => { const hs = hoursOfDay(day); return hs.length > 0 && hourStart(hs[hs.length - 1]) >= V2_MS; };
// a racing day is V3 when it ENDS on or after V3_MS (like dayIsV2): the first day can begin an hour early under the old 10pm day turn
export const dayIsV3 = (day) => { const hs = hoursOfDay(day); return hs.length > 0 && hourStart(hs[hs.length - 1]) >= V3_MS; };
/**
 * V3 — THE CUP NIGHT, slot by slot: { localHour: [[cup, round, index, lane], …] } in the order they're fought on each stage
 * (lane 0 = the Circuit stage, 1 = the Contenders stage). Built so that:
 *  · NOBODY IS IN TWO PLACES AT ONCE — in any hour, the two stages' cup bouts can never share a fighter, whoever wins:
 *    Bottom vs Bottom (Circuit 9–16 + Contenders 9–16) never meets Top vs Top (1–8 + 1–8); the Contenders Cup never meets
 *    the Circuit Cup; one round of one cup is always 8 different fighters (so Last Chance's 16 can split across both stages);
 *    every other hour keeps all its cup bouts on ONE stage.
 *  · THE :50 LAW — a round is never in the same hour as its feeders, and a bout whose winner the NEXT hour needs is among the
 *    first 7 of its stage (worst case, every fight to the 5:00 bell: 2:00 + 7 × 6:25 = 46:55 — under :50; with a final first
 *    and its 2:30 ringside: 7:30 + 6 × 6:25 = 46:00). Eight on one stage only when the next round is 2+ hours away (the last
 *    bell is then 46:55, well inside :53).
 *  · ONE FINAL AN HOUR in Prime Time, least epic first, each opening its hour: 6pm Bottom vs Bottom · 7pm the Contenders Cup ·
 *    8pm Last Chance · 9pm Top vs Top · 10pm the Circuit Cup. (caP'n: "arrange them from least epic to most epic".)
 *   1pm  bb R16 (Contenders stage) | tt R16 (Circuit stage)      2pm  kc R16 | c R16
 *   3pm  lc R16 (4 + 4, both stages)                             4pm  bb QF | tt QF
 *   5pm  bb SF, kc QF                                            6pm  bb FINAL, kc SF, lc QF
 *   7pm  kc FINAL, lc SF, tt SF                                  8pm  lc FINAL, c QF
 *   9pm  tt FINAL, c SF                                          10pm c FINAL, then the NIGHT CROWN (one fight, the Circuit stage)
 * Saturday: 6pm the Losers' Cup QF (first on each stage) + Salvation's R16 (4 + 4) · 7pm l SF · 8pm l FINAL | Salvation QF ·
 * 9pm Salvation SF · 10pm Salvation FINAL. The Season Finale (8) takes Salvation's 8, 9 and 10pm on a season's last Saturday.
 */
const slotRun = (c, r, from, to, ln) => Array.from({ length: to - from + 1 }, (_, n) => [c, r, from + n, ln]);
// a Qualifier's 7 bouts, back to back on ONE stage: 4 quarters, 2 semis, the final (round ≥ 1 is late-bound: its sides are the winners of the bouts before it)
const qualRun = (c, ln) => [...slotRun(c, 0, 0, 3, ln), ...slotRun(c, 1, 0, 1, ln), [c, 2, 0, ln]];
const QUAL_SLOTS = { 11: [...qualRun("q1", 0), ...qualRun("q2", 1)], 12: [...qualRun("q3", 0), ...qualRun("q4", 1)] };
export const V3_FRI_SLOTS = { ...QUAL_SLOTS };   // Friday: the Qualifiers, then the League until Apocalypse at 8pm
export const V3_NIGHT_SLOTS = {
  ...QUAL_SLOTS,
  13: [...slotRun("tt", 0, 0, 7, 0), ...slotRun("bb", 0, 0, 7, 1)],
  14: [...slotRun("c", 0, 0, 7, 0), ...slotRun("kc", 0, 0, 7, 1)],
  15: [...slotRun("lc", 0, 0, 3, 0), ...slotRun("lc", 0, 4, 7, 1)],
  16: [...slotRun("tt", 1, 0, 3, 0), ...slotRun("bb", 1, 0, 3, 1)],
  17: [...slotRun("bb", 2, 0, 1, 1), ...slotRun("kc", 1, 0, 3, 1)],
  18: [["bb", 3, 0, 1], ...slotRun("kc", 2, 0, 1, 1), ...slotRun("lc", 1, 0, 3, 1)],
  19: [["kc", 3, 0, 1], ...slotRun("lc", 2, 0, 1, 1), ...slotRun("tt", 2, 0, 1, 1)],
  20: [["lc", 3, 0, 0], ...slotRun("c", 1, 0, 3, 0)],
  21: [["tt", 3, 0, 0], ...slotRun("c", 2, 0, 1, 0)],
  22: [["c", 3, 0, 0], ["nc", 0, 0, 0]],
};
export const V3_SAT_SLOTS = {
  ...QUAL_SLOTS,
  18: [...slotRun("l", 0, 0, 1, 0), ...slotRun("s", 0, 0, 3, 0), ...slotRun("l", 0, 2, 3, 1), ...slotRun("s", 0, 4, 7, 1)],
  19: [...slotRun("l", 1, 0, 1, 1)],
  20: [["l", 2, 0, 1], ...slotRun("s", 1, 0, 3, 0)],
  21: [...slotRun("s", 2, 0, 1, 0)],
  22: [["s", 3, 0, 0]],
};
// A SEASON'S LAST SATURDAY — BOTH the Season Finale and Salvation (caP'n 2026-10-22: "both <3 a whole day of prime fights would be
// cool"). The Finale's quarters at 2pm and semis at 4pm (alone on the Circuit stage), then the usual Losers' Cup + Salvation from
// 6pm — Salvation a round earlier, final at 9pm — and THE SEASON FINALE FINAL closes the season at 10pm. A Finale fighter can be in
// Salvation too: never in the same hour (the rule is nobody in two places at once).
export const V3_FINALE_DAY_SLOTS = {
  ...QUAL_SLOTS,
  14: slotRun("f", 0, 0, 3, 0),
  16: slotRun("f", 1, 0, 1, 0),
  18: [...slotRun("l", 0, 0, 1, 0), ...slotRun("s", 0, 0, 3, 0), ...slotRun("l", 0, 2, 3, 1), ...slotRun("s", 0, 4, 7, 1)],
  19: [...slotRun("s", 1, 0, 3, 0), ...slotRun("l", 1, 0, 1, 1)],
  20: [...slotRun("s", 2, 0, 1, 0), ["l", 2, 0, 1]],
  21: [["s", 3, 0, 0]],
  22: [["f", 2, 0, 0]],
};
/** A V3 racing day's slots by local hour (weeknights / Saturday / none), with the Finale swapped in on a season's last Saturday. */
function v3SlotsOf(day) {
  if (!dayIsV3(day)) return null;
  const dow = dowOfDay(day);
  if (dow <= 4) return V3_NIGHT_SLOTS;
  if (dow === 5) return V3_FRI_SLOTS;
  if (dow !== 6) return null;
  return weekOfSeason(weekOfDay(day)) !== SEASON_WEEKS ? V3_SAT_SLOTS : V3_FINALE_DAY_SLOTS;
}
/** The cups fought on a racing day, the Circuit's first (V2; none before). V3: the night's five, least epic first. */
export function cupsOfDay(day) {
  if (dayIsV3(day)) {
    const dow = dowOfDay(day);
    if (dow <= 4) return [...QUALS, ...NIGHT_ALL];
    if (dow === 6) return [...QUALS, ...(weekOfSeason(weekOfDay(day)) === SEASON_WEEKS ? ["f", "s", "l"] : ["s", "l"])];
    return [...QUALS];   // Friday: the Qualifiers (Apocalypse is a series, not a cup)
  }
  if (!dayIsV2(day)) return [];
  const dow = dowOfDay(day);
  if (dow <= 4) return ["c", "k"];
  if (dow === 6) return [weekOfSeason(weekOfDay(day)) === SEASON_WEEKS ? "f" : "x", "l"];
  return [];
}
/** The hours a cup is fought in on a racing day, in order (V2 / V3). */
export function cupHours(c, day) {
  if (!cupsOfDay(day).includes(c)) return [];
  const S3 = v3SlotsOf(day);
  if (S3) return hoursOfDay(day).filter((h) => hourInfo(h).v3 && (S3[hourInfo(h).localHour] || []).some((x) => x[0] === c));   // (only V3 hours: week 2's Sunday begins at 10pm Saturday, a plain league hour)
  if (!V2_SLOTS[c]) return [];
  return hoursOfDay(day).filter((h) => { const i = hourInfo(h); return i.v2 && !!V2_SLOTS[c][i.localHour]; });
}
/**
 * The hour a cup's bout (round r, index i) is fought in on a racing day — for the bracket screen's "7pm" / "about when". null if the day
 * has no such bout. (V3: the slot table; V2: V2_SLOTS; before V2: CUP_SLOTS by the cup's hour of that week.)
 */
export function cupSlotHour(c, r, i, day) {
  const S3 = v3SlotsOf(day), hs = hoursOfDay(day);
  // (only V3 hours: the first cup night begins an hour early, at 10pm Saturday, under the old day turn — that hour is a plain league hour)
  if (S3) return hs.find((h) => hourInfo(h).v3 && (S3[hourInfo(h).localHour] || []).some((x) => x[0] === c && x[1] === r && x[2] === i)) ?? null;
  if (dayIsV2(day)) return hs.find((h) => { const n = hourInfo(h); return n.v2 && !!V2_SLOTS[c] && (V2_SLOTS[c][n.localHour] || []).some((x) => x[0] === r && x[1] === i); }) ?? null;
  if (!CUPS[c]) return null;
  const idx = CUP_SLOTS.findIndex((sl) => sl.some((x) => x[0] === r && x[1] === i));
  return idx < 0 ? null : (kindHours(weekOfDay(day), CUPS[c].kind)[idx] ?? null);
}
/** Does hour h's card need the whole season's rows? (the Finale seeds by the season — and on V2's last Saturday the
 *  Losers' Cup, drawn at 6pm, must leave out the Finale's eight, so it needs them too) */
export const needsSeasonRows = (info) => info.kind === "finale" || (info.v2 && info.dow === 6 && weekOfSeason(info.week) === SEASON_WEEKS);   // (a last Saturday: every hour may draw the Finale's field — the Losers' Cup leaves it out)
/** The racing day a week's Saturday cups are on. */
export const saturdayOf = (week) => week * 7 + 2;
/**
 * When a cup's field is drawn (V2): a nightly cup at its own first hour; BOTH Saturday cups at the Losers' Cup's
 * first hour (6pm), so the two fields come off one table and never overlap (the Losers' Cup is for whoever
 * isn't in the Champions Cup). Only the rows that hour's card KNOWS count (isKnown), so every hour of a cup
 * draws the exact same field.
 */
export const cutOf = (c, day) => {
  if (dayIsV3(day)) {
    // V3: all five of a night are drawn off ONE table at the night's first cup card (1pm), so "top half" means the same in
    // every cup; Saturday's two at 6pm. c "n" = the night's single check-in (entryCupOf).
    if (c === "nc") return cupHours("nc", day)[0];   // the Night Crown is drawn at its own hour's card (:50 before 10pm), off who the night has crowned by then
    // (the Qualifiers: all four pools are dealt at the first Qualifier's card (11am) — and the night's check-in "n" closes with it, so nobody joins a pool late)
    if (isQual(c) || c === "n") return cupHours("q1", day)[0] ?? cupHours("bb", day)[0] ?? cupHours("tt", day)[0];
    if (NIGHT_CUPS.includes(c) && dowOfDay(day) <= 4) return cupHours("bb", day)[0] ?? cupHours("tt", day)[0];
    if (c === "f") return cupHours("f", day)[0];   // the Finale draws at its own first card (2pm on a last Saturday)
    if (c === "s" || c === "l") return cupHours("l", day)[0];
    return undefined;
  }
  return c === "x" || c === "f" || c === "l" ? cupHours("l", day)[0] : cupHours(c, day)[0];
};
/** Which check-in a cup reads (V3: one for the whole night, "n" — caP'n's five cups are one sign-up). */
export const entryCupOf = (c, day) => (dayIsV3(day) && ((NIGHT_CUPS.includes(c) && dowOfDay(day) <= 4) || isQual(c)) ? "n" : c);
/** The check-ins a racing day has, in order (what a Challenger's screen offers): V3 weeknight ["n"]; else its cups. */
export const checkInsOfDay = (day) => (!dayIsV3(day) ? cupsOfDay(day) : dowOfDay(day) <= 4 ? ["n"] : ["n", ...cupsOfDay(day).filter((c) => !isQual(c))]);   // (every V3 day: "n" = the Qualifiers (+ the night's cups Sun–Thu); Saturday's cups are their own)
const isNpcKey = (k) => BEINGS.has(k);
/**
 * Who may be drawn into a cup: every NPC of the right division; a CHALLENGER (a player or partner) only if
 * they checked in for that cup (ctx.entered(c, day, key) — the check-in closes when its first card goes up).
 */
const enteredOf = (ctx, c, day) => (k) => isNpcKey(k) || !!(ctx.entered && ctx.entered(entryCupOf(c, day), day, k));
/**
 * The week's cup winners before Saturday, best first: Circuit Cup winners (most wins, then the table), then Contenders Cup
 * winners. Each night's champion is read off that night's own bracket (cupPlan), never off a loose final row.
 * ctx = the cups' ctx (league, weekRows, entered).
 */
export function weekChampions(rows, week, S, league, ctx = null) {
  const out = [], c2 = { league, weekRows: rows, entered: (ctx && ctx.entered) || null };
  for (const c of ["c", "k"]) {
    const won = new Map();
    for (let d = week * 7 - 4; d < saturdayOf(week); d++) {
      if (!cupsOfDay(d).includes(c)) continue;
      const k = cupPlanV2(c, d, c2).champion;
      if (k) won.set(k, (won.get(k) || 0) + 1);
    }
    const order = rankKeys(c === "c" ? league.circuit : league.contenders, S);
    const rank = (k) => { const i = order.indexOf(k); return i < 0 ? 999 : i; };
    [...won.keys()].sort((a, b) => won.get(b) - won.get(a) || rank(a) - rank(b) || (a < b ? -1 : 1)).forEach((k) => out.push({ c, key: k, n: won.get(k) }));
  }
  return out;
}
/** A V2 cup's field, best seed first (≤ its size). V3: the cup's keys in BRACKET order (cupFieldV3). */
export function cupField(c, day, ctx) {
  if (dayIsV3(day)) return cupFieldV3(c, day, ctx).field;
  const { league } = ctx, cut = cutOf(c, day);
  if (cut == null || !league) return [];
  const rows = knownRows(cleanRows(ctx.weekRows || []).filter((r) => r.h < cut), cut);
  const S = standings(rows, league.elo || null), entered = enteredOf(ctx, c, day);
  // a Challenger needs QUALIFY bouts on the week's table to be seeded (caP'n: "if player/partner is eligible … because they won enough")
  const ok = (k) => entered(k) && (isNpcKey(k) || (() => { const s = statOf(S, k); return s.lw + s.ll >= QUALIFY; })());
  if (c === "k") return rankKeys(league.contenders.filter(ok), S).slice(0, CUPS.k.size);
  if (c === "c") return rankKeys(league.circuit.filter(ok), S).slice(0, 16);
  if (c === "f") {
    const sr = knownRows(cleanRows(ctx.seasonRows || ctx.weekRows || []).filter((r) => r.h < cut), cut);
    const Ss = standings(sr);
    return rankKeys(league.circuit.filter((k) => entered(k) && (isNpcKey(k) || statOf(Ss, k).cw + statOf(Ss, k).cl >= QUALIFY)), Ss, REC.circuit).slice(0, CUP_SIZE);
  }
  if (c === "x") {
    const out = [];
    for (const { key } of weekChampions(rows, weekOfDay(day), S, league, ctx)) if (ok(key) && !out.includes(key)) out.push(key);
    for (const k of rankKeys(league.circuit.filter(ok), S)) if (!out.includes(k)) out.push(k);
    return out.slice(0, CUP_SIZE);
  }
  if (c === "l") {
    const top = new Set(cupField(weekOfSeason(weekOfDay(day)) === SEASON_WEEKS ? "f" : "x", day, ctx));
    const elo = (k) => statOf(S, k).elo;
    const byElo = (a, b) => elo(b) - elo(a) || castOrder(a) - castOrder(b) || (a < b ? -1 : 1);
    // a free-for-all for players: every Challenger who checked in and isn't in the Champions Cup, then the bottom of the Circuit
    const players = [...league.circuit, ...league.contenders].filter((k) => !isNpcKey(k) && entered(k) && !top.has(k)).sort(byElo).slice(0, CUP_SIZE);   // no minimum: a free-for-all
    const fill = rankKeys(league.circuit.filter(isNpcKey), S).reverse().filter((k) => !top.has(k));
    return [...players, ...fill].slice(0, CUP_SIZE).sort(byElo);
  }
  return [];
}
/**
 * A cup's bracket, from the rows. The field is fixed when the cup is drawn (the table as it stood);
 * a round is filled as its feeders are decided. weekRows = this week's rows; seasonRows for the Finale.
 * V2 cups need the racing day (a nightly cup happens five times a week): cupPlan(c, week, ctx, day) —
 * Saturday's cups default to the week's Saturday. A missing seed is a BYE (its opponent goes through).
 * Resolves { c, day, hours, keys:[bracket order], bout(r,i) → { a, b } | null, champion, rounds }.
 */
export function cupPlan(c, week, ctx, day = null) {
  const v2 = c === "k" || c === "c" || c === "x" || NIGHT_ALL.includes(c) || isQual(c) || c === "s" || (dayIsV2(saturdayOf(week)) && (c === "l" || c === "f"));
  if (v2) return cupPlanV2(c, day == null ? saturdayOf(week) : day, ctx);
  const { league, weekRows, seasonRows = null } = ctx;
  const hours = kindHours(week, CUPS[c].kind);
  const first = hours[0] ?? Infinity;
  // the table as the cup's FIRST card knew it — the same field however late a later round's card is planned
  const before = (rows) => knownRows(cleanRows(rows).filter((r) => r.h < first), first);
  let ranked;
  if (c === "w") ranked = rankKeys(league.circuit, standings(before(weekRows))).slice(0, CUP_SIZE);
  else if (c === "l") ranked = rankKeys(league.circuit, standings(before(weekRows))).slice(-CUP_SIZE);
  else ranked = rankKeys(league.circuit, standings(before(seasonRows || weekRows)), REC.circuit).slice(0, CUP_SIZE);
  const keys = SEED_ORDER.map((i) => ranked[i]).filter(Boolean);
  const mine = cleanRows(weekRows).filter((r) => r.kind === "cup" && r.c === c);
  const won = (r, i) => { const row = mine.find((x) => x.r === r && x.i === i); return row ? (row.w === 0 ? row.a : row.b) : null; };
  const bout = (r, i) => {
    const a = r === 0 ? keys[2 * i] : won(r - 1, 2 * i), b = r === 0 ? keys[2 * i + 1] : won(r - 1, 2 * i + 1);
    return a && b ? { a, b } : null;
  };
  // (sides: a key · null = an empty seat · undefined = the feeder bout isn't decided yet — the bracket screen draws a tree from it)
  const sides = (r, i) => (r === 0 ? { a: keys[2 * i] || null, b: keys[2 * i + 1] || null } : { a: won(r - 1, 2 * i) || undefined, b: won(r - 1, 2 * i + 1) || undefined });
  return { c, day: null, hours, keys, field: ranked, bout, winner: won, sides, champion: won(2, 0), rounds: 3 };
}
function cupPlanV2(c, day, ctx) {
  const hours = cupHours(c, day), R = roundsOf(c);
  let field, keys, na = null;
  if (dayIsV3(day)) ({ field, keys, na = null } = cupFieldV3(c, day, ctx));
  else { field = cupField(c, day, ctx); keys = (CUPS[c].size === 16 ? SEED16 : SEED_ORDER).map((i) => field[i] || null); }
  const mine = cleanRows(ctx.weekRows || []).filter((r) => r.kind === "cup" && r.c === c && racingDayOf(hourStart(r.h)) === day);
  // who comes out of a bout: a key · null = nobody (both seats empty) · undefined = not decided yet
  const memo = new Map();
  const sides = (r, i) => (r === 0 ? { a: keys[2 * i], b: keys[2 * i + 1] } : { a: adv(r - 1, 2 * i), b: adv(r - 1, 2 * i + 1) });
  function adv(r, i) {
    const m = `${r}:${i}`;
    if (memo.has(m)) return memo.get(m);
    const s = sides(r, i);
    let v;
    if (s.a === undefined || s.b === undefined) v = undefined;
    else if (s.a && s.b) { const row = mine.find((x) => x.r === r && x.i === i && ((x.a === s.a && x.b === s.b) || (x.a === s.b && x.b === s.a))); v = row ? (row.w === 0 ? row.a : row.b) : undefined; }
    else v = s.a || s.b || null;   // a bye (or nobody at all)
    memo.set(m, v);
    return v;
  }
  const bout = (r, i) => { const s = sides(r, i); return typeof s.a === "string" && typeof s.b === "string" ? { a: s.a, b: s.b } : null; };
  const ch = adv(R - 1, 0);
  // who won bout (r, i) — a key once it's fought (or a bye went through), else null
  const winner = (r, i) => { const s = sides(r, i), v = adv(r, i); return typeof s.a === "string" && typeof s.b === "string" && typeof v === "string" ? v : null; };
  return { c, day, hours, keys, field, na, bout, winner, sides, champion: typeof ch === "string" ? ch : null, rounds: R };
}
// ════════════════════════════════════════════════════════════
//  LATE-BOUND BOUTS — a round that rolls in the SAME hour as the one before it (caP'n 2026-10-23: "after a tournament ends maybe 3-5 minutes down
//  time … just go"). The hour's card is locked at :50 of the hour before, so a bout whose fighters are decided BY a bout in that very hour can't name
//  them yet: it carries the feeder instead — { a: "npc3", fb: "0:1" } = side a is npc3, side b is the winner of this cup's round 0, bout 1. The card
//  stays a pure, locked list; the stage (and the keeper's paper hours) read the winners as the bouts end. A later round's matchup is thus known only
//  when the fight before it ends (bets open ~1:30, not ten minutes ahead). Only Qualifiers are late-bound; the cups' rounds keep their own hours.
// ════════════════════════════════════════════════════════════
/** A bout of the card whose fighters aren't both named (a side waits on a bout of the same hour). */
export const isBound = (b) => !!b && (typeof b.a !== "string" || typeof b.b !== "string");
/** The ids of the bouts a late-bound bout waits on: ["q1:0:0", "q1:0:1"] (the sides still missing). */
export const feedersOf = (b) => [typeof b.a === "string" ? null : b.fa, typeof b.b === "string" ? null : b.fb].map((x) => (x ? `${b.c}:${x}` : null));
/**
 * A card bout's two fighters given the winners decided so far (Map("c:r:i" → key)): { a, b } or null while a feeder is undecided.
 * A bout that already names both sides is handed back as it is.
 */
export function resolveBound(b, winners) {
  if (!isBound(b)) return { a: b.a, b: b.b };
  const side = (k, f) => (typeof k === "string" ? k : f ? winners.get(`${b.c}:${f}`) || null : null);
  const a = side(b.a, b.fa), c = side(b.b, b.fb);
  return a && c ? { a, b: c } : null;
}
/** A fought bout's winner key, filed under its bout id for resolveBound. */
export const boutKey = (c, r, i) => `${c}:${r}:${i}`;
/** Where a cup bout's seat comes from, with byes passed through: { k: key } · { f: [r, i] } (a real bout feeds it) · null (nobody). */
function symSide(P, r, i, s) {
  if (r === 0) { const k = P.keys[2 * i + s]; return k ? { k } : null; }
  const x = 2 * i + s, p = symSide(P, r - 1, x, 0), q = symSide(P, r - 1, x, 1);
  return p && q ? { f: [r - 1, x] } : p || q || null;
}
/** A Qualifier bout as a card entry (a, b and/or fa, fb); null when it isn't a real bout (a bye or an empty seat). */
function qualBout(P, r, i) {
  const A = symSide(P, r, i, 0), B = symSide(P, r, i, 1);
  if (!A || !B) return null;
  const sd = (x, key, f) => (x.k ? { [key]: x.k } : { [f]: `${x.f[0]}:${x.f[1]}` });
  return { ...sd(A, "a", "fa"), ...sd(B, "b", "fb") };
}
const fi0 = (P, k) => { const x = (P.field || []).indexOf(k); return x < 0 ? 99 : x; };
/** The cup bouts that belong on hour h's card (features). */
function cupFeatures(h, info, ctx) {
  const S3 = info.v3 ? v3SlotsOf(info.day) : null;
  if (S3) {   // V3: the slot table says which bout, and on which stage (ln)
    const out = [], plans = new Map();
    for (const [c, r, i, ln] of S3[info.localHour] || []) {
      if (!plans.has(c)) plans.set(c, cupPlanV2(c, info.day, ctx));
      const P = plans.get(c);
      if (isQual(c)) {   // a Qualifier rolls inside its hour: a seat that a bout in this hour feeds is LATE-BOUND (isBound) — its fighter is read off that bout's winner
        const q = qualBout(P, r, i);
        if (q) out.push({ ...q, k: "cup", c, r, i, ln, sd: Math.min(...[q.a, q.b].map((k) => (k ? fi0(P, k) : 99)), 99) });
        continue;
      }
      const b = P.bout(r, i);
      if (!b) continue;
      // THE CLIMAX LAW (caP'n 2026-10-22): sd = the bout's best seed (0 = the cup's #1) so a round can put its #1 LAST; cl = a semifinal or
      // final (it closes the stage's hour — see planHourV3); nx = the next round is in the very next hour, so this bout must END by :50
      const fi = (k) => { const x = (P.field || []).indexOf(k); return x < 0 ? 99 : P.na != null && x >= P.na ? x - P.na : x; };   // (a mixed cup has two #1s: each division's own rank)
      // (a FINAL feeds the next hour too when that hour holds the Night Crown: its champion is a seat there, and that card goes up at :50 — Top vs Top's final, 9pm)
      const nx = (r < finalR(c) && (S3[info.localHour + 1] || []).some((y) => y[0] === c && y[1] === r + 1))
        || (r === finalR(c) && c !== "c" && c !== "nc" && NIGHT_CUPS.includes(c) && (S3[info.localHour + 1] || []).some((y) => y[0] === "nc"));
      out.push({ ...b, k: "cup", c, r, i, ln, sd: Math.min(fi(b.a), fi(b.b)), ...(r >= finalR(c) - 1 ? { cl: 1 } : {}), ...(nx ? { nx: 1 } : {}) });
    }
    return out;
  }
  if (info.v3) return [];
  if (info.v2) {
    const out = [];
    for (const c of cupsOfDay(info.day)) {
      const sl = V2_SLOTS[c][info.localHour];
      if (!sl) continue;
      const plan = cupPlanV2(c, info.day, ctx);
      for (const [r, i] of sl) { const b = plan.bout(r, i); if (b) out.push({ ...b, k: "cup", c, r, i }); }
    }
    return out;
  }
  const c = CUP_OF_KIND[info.kind];
  if (!c) return [];
  const plan = cupPlan(c, info.week, ctx);
  const idx = plan.hours.indexOf(h);
  return (CUP_SLOTS[idx] || []).map(([r, i]) => { const b = plan.bout(r, i); return b && { ...b, k: "cup", c, r, i }; }).filter(Boolean);
}

// ════════════════════════════════════════════════════════════
//  V3 — THE NIGHT'S FIELDS, THE ESCAPES, SALVATION (caP'n 2026-10-21/22)
// ════════════════════════════════════════════════════════════
//  "every single night actually the winner of Contenders Night Cup fights the bottom of the Circuit for a chance to escape"
//  → five cups a night; "1 top half of contenders vs top half of circuit (another way to escape early, 1 escape)",
//  "1 last chance, top half of contenders vs bottom half of the circuit (1 escape)". "They escape after the night's prime
//  time cups are over." Escapes are read straight off the ledger (the two escape finals' rows), so every screen agrees.
// ════════════════════════════════════════════════════════════
// a small memo for the folds that every card and board re-asks (keyed by the rows they were handed — same rows, same answer)
const rowsSig = (rows) => { let x = rows.length; for (const r of rows) x = (Math.imul(x, 31) + strSeed(r.id)) | 0; return `${rows.length}:${x}`; };
const V3MEMO = new Map();
export const clearV3Memo = () => V3MEMO.clear();   // (tools only: a dial changed between runs)
const v3memo = (key, f) => { if (V3MEMO.has(key)) return V3MEMO.get(key); const v = f(); if (V3MEMO.size > 600) V3MEMO.clear(); V3MEMO.set(key, v); return v; };
const leagueSig = (L) => `${L.circuit.join(",")}|${L.contenders.join(",")}`;
const eloSig = (e) => (e ? strSeed(JSON.stringify(e)) : 0);
const deepFreeze = (o) => { if (o && typeof o === "object" && !Object.isFrozen(o)) { Object.freeze(o); for (const v of Object.values(o)) deepFreeze(v); } return o; };
/** A division's week table (V3: only the bouts fought IN that division count — people change division mid-week now). */
export const divRec = (dv) => (dv === "c" ? REC.circuit : REC.contenders);
/**
 * THE LEAGUE ON A RACING DAY (V3): the week's stored lists + every escape of the nights before `day` this week.
 * An escape: the winner of Last Chance's or Top vs Top's final who was a Contender that night takes the place of the
 * Circuit's LAST on its table (its own bouts only), as the next night's first card knows it (isKnown). This week's escapees
 * can't be dropped again this week (they earned it). One escape per Contender a night (winning both cups is still one seat).
 * Resolves { …league, escaped:[keys this week], dropped:[keys], nights:[{ day, up:[keys], down:[keys] }] }.
 * Before V3 (or a day with no escapes before it) the league is handed back with empty lists.
 */
export function leagueOn(league, weekRows, day) {
  if (!league) return league;
  league = league.base || league;   // handed a league that's already "on a day": start again from the week's own lists (never apply escapes twice)
  const base = { ...league, base: league, escaped: [], dropped: [], nights: [] };
  if (!dayIsV3(day)) return base;
  const first = weekOfDay(day) * 7 - 4;
  if (day <= first) return base;
  const rows = cleanRows(weekRows || []);
  const lists = v3memo(`on:${day}:${leagueSig(league)}:${rowsSig(rows)}`, () => {
    let L = { circuit: base.circuit, contenders: base.contenders, escaped: [], dropped: [], nights: [] };
    for (let n = first; n < day; n++) if (dayIsV3(n) && dowOfDay(n) <= 4) L = escapeNight(L, rows, n);
    return deepFreeze({ circuit: L.circuit, contenders: L.contenders, escaped: L.escaped, dropped: L.dropped, nights: L.nights });
  });
  return { ...base, ...lists };
}
function escapeNight(L, rows, n) {
  const fin = rows.filter((r) => r.kind === "cup" && CUPS[r.c] && CUPS[r.c].escape && r.r === finalR(r.c) && racingDayOf(hourStart(r.h)) === n);
  const ups = [];
  for (const r of fin) { const k = winnerOf(r); if (L.contenders.includes(k) && !ups.includes(k)) ups.push(k); }   // Last Chance (8pm) first, then Top vs Top
  if (!ups.length) return L;
  const h1 = hoursOfDay(n + 1)[0], immune = new Set(L.escaped);
  const S = standings(knownRows(rows.filter((r) => r.h < h1), h1));
  // who falls: the Circuit's last among those who've fought QUALIFY Circuit bouts this week (review 2026-10-22: a Challenger who
  // missed a day sat "last" at 0–0 and fell to the first escape). The unqualified only when nobody qualified is left; a no-show
  // still faces Friday's Apocalypse (use it or lose it — caP'n 2026-10-21).
  const downs = dropOrder(L.circuit, S, immune).slice(0, ups.length);
  const up = ups.slice(0, downs.length);
  return {
    ...L,
    circuit: L.circuit.filter((k) => !downs.includes(k)).concat(up),
    contenders: L.contenders.filter((k) => !up.includes(k)).concat(downs),
    escaped: [...L.escaped, ...up], dropped: [...L.dropped, ...downs], nights: [...L.nights, { day: n, up, down: downs }],
  };
}
/** Who an escape pushes out of the Circuit, first to fall first: the qualified bottom up, then the unqualified. */
function dropOrder(circuit, S, immune) {
  const r = rankKeys(circuit, S, REC.circuit).filter((k) => !immune.has(k)), q = (k) => { const [w, l] = REC.circuit(statOf(S, k)); return w + l >= QUALIFY; };
  return [...r.filter(q).reverse(), ...r.filter((k) => !q(k)).reverse()];
}
// a mixed cup's bracket: A = the Circuit's half (best first), B = the Contenders' half — every first-round bout is
// Circuit vs Contenders; A1/A2 and B1/B2 sit in opposite halves (A1 v B8 · B4 v A5 · A4 v B5 · B1 v A8 | A2 v B7 · B3 v A6 · A3 v B6 · B2 v A7)
const MIX16 = [["A", 0], ["B", 7], ["B", 3], ["A", 4], ["A", 3], ["B", 4], ["B", 0], ["A", 7], ["A", 1], ["B", 6], ["B", 2], ["A", 5], ["A", 2], ["B", 5], ["B", 1], ["A", 6]];
/**
 * A V3 cup's field: { field: [keys, best first], keys: [bracket order, null = a bye] }. The night's five are drawn off ONE
 * table (the night's cut, 1pm): each division ranked by its own bouts this week, the halves its top 8 and next 8. Who may be
 * drawn: every NPC; a Challenger who checked in for the night (or for Saturday's cup) AND has QUALIFY bouts on their
 * division's table this week. SALVATION: everyone at or over the points line (salvationOf), best score first, capped at 16.
 */
export function cupFieldV3(c, day, ctx) {
  const cut = cutOf(c, day), none = { field: [], keys: Array(CUPS[c] ? CUPS[c].size : 8).fill(null) };
  if (cut == null || !ctx.league) return none;
  const rows = knownRows(cleanRows(ctx.weekRows || []).filter((r) => r.h < cut), cut);
  const L = leagueOn(ctx.league, rows, day), S = standings(rows, ctx.league.elo || null), entered = enteredOf(ctx, c, day);
  const qual = (k, dv) => isNpcKey(k) || (() => { const [w, l] = divRec(dv)(statOf(S, k)); return w + l >= QUALIFY; })();
  let circ = rankKeys(L.circuit.filter((k) => entered(k) && qual(k, "c")), S, REC.circuit);
  let cont = rankKeys(L.contenders.filter((k) => entered(k) && qual(k, "k")), S, REC.contenders);
  // THE QUALIFIERS ORDER THE NIGHT (caP'n 2026-10-23: "winning these baby tourneys … is how you qualify for the real prime time"): the cups are drawn at 1pm, after the
  // four Qualifiers (11am, noon). Each division's order is HOW FAR YOU GOT (qualTiers: winner → finalist → semifinalist → lost in the quarters), the table breaking ties and
  // ranking everyone the Qualifiers didn't take (a Challenger outside the top 16). So the Qualifiers' semifinalists are the division's top 8 — the seats of Top vs Top —
  // and the table fills what's left. The Qualifiers themselves are dealt off the table as it stood at THEIR cut (11am).
  if (isQual(c)) {
    const list = (CUPS[c].dv === "c" ? circ : cont).slice(0, 16), pool = (CUPS[c].pool ? QUAL_POOL_B : QUAL_POOL_A).map((i) => list[i]).filter(Boolean);
    return { field: pool, keys: SEED_ORDER.map((i) => pool[i] || null) };
  }
  if (NIGHT_CUPS.includes(c) && dowOfDay(day) <= 4) {
    const T = qualTiers(day, rows, ctx), by = (xs) => xs.map((k, n) => ({ k, n })).sort((x, y) => (T.get(y.k) ?? -1) - (T.get(x.k) ?? -1) || x.n - y.n).map((x) => x.k);
    if (T.size) { circ = by(circ); cont = by(cont); }
  }
  const half = { c: circ.slice(0, 16), k: cont.slice(0, 16), ct: circ.slice(0, 8), cb: circ.slice(8, 16), kt: cont.slice(0, 8), kb: cont.slice(8, 16) };
  const seeded = (list) => SEED16.map((i) => list[i] || null);
  const mixed = (A, B) => MIX16.map(([s, i]) => (s === "A" ? A : B)[i] || null);
  if (c === "nc") return nightCrownField(day, rows, ctx);
  if (c === "c" || c === "kc") { const f = half[c === "c" ? "c" : "k"]; return { field: f, keys: seeded(f) }; }
  if (CUPS[c] && CUPS[c].sides && CUPS[c].sides.length === 2) { const [a, b] = CUPS[c].sides, A = half[a], B = half[b]; return { field: [...A, ...B], keys: mixed(A, B), na: A.length }; }   // (na: where the second division's half begins in the field — a mixed cup has two #1s)
  if (c === "s") {
    const P = salvationOf(ctx.league, rows, ctx.league.elo || null);
    const T = dowOfDay(day) === 6 ? qualTiers(day, rows, ctx) : new Map();   // (Saturday's Qualifiers: the winner and the finalist)
    const extra = [...T.entries()].filter(([, t]) => t >= 2).sort((x, y) => y[1] - x[1] || castOrder(x[0]) - castOrder(y[0]) || (x[0] < y[0] ? -1 : 1)).map(([k]) => k);
    const f = salvationField(P, (k) => entered(k), salvationLine(P, ctx.league), standoutLine(P, L), extra);
    return { field: f, keys: seeded(f) };
  }
  if (c === "f") {   // the Season Finale: the season's top 8 of the Circuit (as before), the Circuit as it stands on Saturday
    const sr = knownRows(cleanRows(ctx.seasonRows || ctx.weekRows || []).filter((r) => r.h < cut), cut), Ss = standings(sr);
    const f = rankKeys(L.circuit.filter((k) => entered(k) && (isNpcKey(k) || statOf(Ss, k).cw + statOf(Ss, k).cl >= QUALIFY)), Ss, REC.circuit).slice(0, CUP_SIZE);
    return { field: f, keys: SEED_ORDER.map((i) => f[i] || null) };
  }
  if (c === "l") {   // bragging rights: every Challenger who checked in and isn't in Salvation / the Finale, then the Circuit's bottom
    const top = new Set([...cupFieldV3("s", day, ctx).field, ...(weekOfSeason(weekOfDay(day)) === SEASON_WEEKS ? cupFieldV3("f", day, ctx).field : [])]);   // nobody in Salvation or the Finale
    const elo = (k) => statOf(S, k).elo, byElo = (a, b) => elo(b) - elo(a) || castOrder(a) - castOrder(b) || (a < b ? -1 : 1);
    const players = [...L.circuit, ...L.contenders].filter((k) => !isNpcKey(k) && entered(k) && !top.has(k)).sort(byElo).slice(0, CUP_SIZE);
    // filled with the Circuit's bottom who aren't in Salvation — and, when Salvation took most of the Circuit, the Contenders' best
    const fill = [...rankKeys(L.circuit.filter(isNpcKey), S, REC.circuit).reverse(), ...rankKeys(L.contenders.filter(isNpcKey), S, REC.contenders)].filter((k) => !top.has(k));
    const f = [...players, ...fill].slice(0, CUP_SIZE).sort(byElo);
    return { field: f, keys: SEED_ORDER.map((i) => f[i] || null) };
  }
  return none;
}
/**
 * HOW FAR A FIGHTER GOT IN THE NIGHT'S QUALIFIERS: Map(key → 3 won it · 2 lost the final · 1 lost a semifinal (or walked through a bye to one) · 0 lost in the quarters).
 * Read off the rows the asking card knows (cupFieldV3 hands them cut), so every screen draws the same order. Nobody who wasn't in a Qualifier is in it.
 */
function qualTiers(day, rows, ctx) {
  const inSig = (() => { const en = enteredOf(ctx, "bb", day), L = ctx.league.base || ctx.league; return [...(L.circuit || []), ...(L.contenders || [])].filter((k) => !isNpcKey(k)).map((k) => (en(k) ? 1 : 0)).join(""); })();
  return v3memo(`qt:${day}:${leagueSig(ctx.league.base || ctx.league)}:${eloSig(ctx.league.elo || null)}:${rowsSig(rows)}:${inSig}`, () => {
    const kctx = { ...ctx, weekRows: rows }, out = new Map();
    for (const q of QUALS) {
      const P = cupPlanV2(q, day, kctx), reach = new Map();
      for (let r = 0; r < P.rounds; r++) for (let i = 0; i < CUPS[q].size >> (r + 1); i++) {
        const sd = P.sides(r, i);
        for (const k of [sd.a, sd.b]) if (typeof k === "string") reach.set(k, Math.max(reach.get(k) ?? 0, r));
      }
      for (const [k, r] of reach) out.set(k, k === P.champion ? 3 : r);
    }
    return deepFreeze(out);
  });
}
/**
 * THE NIGHT CROWN'S FIELD (caP'n 2026-10-22: "the final tournament of the night with the previous winners … and the day's performances"):
 * the night's earlier champions (Bottom vs Bottom, the Contenders Cup, Last Chance, Top vs Top — whoever has won one by the 9:50 card),
 * best NIGHT first: the points the night's own bouts paid (a cup champion's bonus, streaks, who they beat — points.js), then the bigger cup,
 * then the cast order. The first two fight. A champion who is ALSO in the Circuit Cup final fights that first and the Night Crown after it — one
 * stage, one bout after the other, never two places at once (the 9pm hour already runs a Top vs Top final beside Circuit Cup semifinals that way) — so
 * the field never waits on the Circuit Cup's result (the card goes up at :50, before it's known). Fewer than two champions → an empty seat (no
 * crown is handed out without a fight).
 * `rows` = the rows the Night Crown's own card knows (cupFieldV3 already cut them), so every screen draws the same two.
 */
function nightCrownField(day, rows, ctx) {
  const inSig = (() => { const en = enteredOf(ctx, "bb", day), L = ctx.league.base || ctx.league; return [...(L.circuit || []), ...(L.contenders || [])].filter((k) => !isNpcKey(k)).map((k) => (en(k) ? 1 : 0)).join(""); })();   // (who has checked in changes the champions' fields: it is part of the key)
  return v3memo(`nc:${day}:${leagueSig(ctx.league.base || ctx.league)}:${eloSig(ctx.league.elo || null)}:${rowsSig(rows)}:${inSig}`, () => {
    const kctx = { ...ctx, weekRows: rows };
    const champs = [];
    for (const cc of NIGHT_CUPS) { if (cc === "c") continue; const k = cupPlanV2(cc, day, kctx).champion; if (k) champs.push({ k, c: cc }); }
    const night = rows.filter((r) => racingDayOf(hourStart(r.h)) === day), P = night.length ? salvationOf(ctx.league, night, ctx.league.elo || null) : new Map();
    const sc = (k) => (P.get(k) ? P.get(k).score : 0);
    const f = [];
    for (const x of champs.slice().sort((a, b) => sc(b.k) - sc(a.k) || CUPS[b.c].tier - CUPS[a.c].tier || castOrder(a.k) - castOrder(b.k) || (a.k < b.k ? -1 : 1))) if (!f.includes(x.k)) f.push(x.k);   // (one person can win two cups: they hold one seat)
    return deepFreeze({ field: f, keys: f.length < 2 ? [null, null] : [f[0], f[1]] });   // (a lone champion is NOT a bye: no fight, no crown)
  });
}
/**
 * THE POINTS of a week (points.js does the scoring; this feeds it): every bout's winner earns points in the LOSER's
 * division that day (caP'n: "sounds good partner" — beat a Circuit fighter, bank Circuit points), plus each cup's
 * champion, each escape and each Apocalypse series winner. Resolves Map(key → { cp, kp, score, … }) (pointsFold's result).
 */
export function salvationOf(league, weekRows, eloStart = null) {
  const rows = cleanRows(weekRows || []);
  if (!league || !rows.length) return new Map();
  return v3memo(`pts:${leagueSig(league.base || league)}:${eloSig(eloStart)}:${rowsSig(rows)}`, () => {
    const F = pointsFold(), byDay = new Map();
    const Lday = (d) => { if (!byDay.has(d)) byDay.set(d, leagueOn(league, rows, d)); return byDay.get(d); };
    const divOf = (k, row) => (row.kind === "lb" ? row.dv : Lday(racingDayOf(hourStart(row.h))).circuit.includes(k) ? "c" : "k");
    standings(rows, eloStart, (row, info) => {
      F.bout(row, info, divOf, CUPS[row.c] ? CUPS[row.c].tier || 0 : 0);
      if (row.kind === "cup" && row.r === finalR(row.c)) F.champion(winnerOf(row), row.c, divOf(loserOf(row), row), row);
    });
    const week = weekOf(rows[rows.length - 1].at);
    for (const n of Lday(saturdayOf(week)).nights) for (const k of n.up) F.escape(k, n.day);
    if (dayIsV3(saturdayOf(week))) {
      for (const s of apocalypsePlan(league, rows, week).series) if (s.done) F.series(s.winner, s);
    }
    // THE PACE: a player or partner who fought fewer bouts than the NPCs' usual week is scored at their pace (points.js paced)
    const out = F.result(), S = standings(rows), npc = [...S.values()].filter((s) => isNpcKey(s.key)).map((s) => s.played).sort((a, b) => a - b);
    const usual = npc.length ? npc[npc.length >> 1] : 0;
    for (const [k, v] of out) if (isChKey(k)) out.set(k, paced(v, statOf(S, k).played, usual));   // players and partners only (the NPCs fight a full week)
    // THE CROWN'S WAY IN: a cup won this week (tier ≥ CROWN_ENTRY_TIER) is a crown, and a crown is a seat in Salvation — merit, like the standout's
    for (const row of rows) {
      if (row.kind !== "cup" || !CUPS[row.c] || row.r !== finalR(row.c) || crownTier(row.c) < CROWN_ENTRY_TIER) continue;
      const k = winnerOf(row), v = out.get(k);
      if (v) out.set(k, { ...v, crowns: (v.crowns || 0) + 1 });
    }
    return out;
  });
}
/** THE LINE (points.js SALVATION_LINE): that × the middle score of the NPCs who began the week in the Circuit. */
export function salvationLine(P, league) {
  const base = (league && (league.base || league).circuit) || [];
  const xs = base.filter(isNpcKey).map((k) => (P.get(k) ? P.get(k).score : 0)).sort((a, b) => a - b);
  if (!xs.length) return Infinity;
  const mid = xs.length % 2 ? xs[xs.length >> 1] : (xs[xs.length / 2 - 1] + xs[xs.length / 2]) / 2;
  return Math.round(SALVATION_LINE * mid);
}
/** Who makes Salvation from a points table: at or over the line (or a standout Contender — standoutLine), best score first, ≤ 16 — and never fewer than
 *  SALVATION_MIN (the best scores fill it up to that, so the King's Cup is always a real bracket). ok(k) = may enter. */
/**
 * THE STANDOUT'S LINE (points.js STANDOUT_LINE): { keys: who's in the Contenders on the day, kp: the Contender points to reach }
 * = that × the middle Contender points of everyone in the Contenders. A standout gets in on merit, not on the Circuit's scale.
 */
export function standoutLine(P, L) {
  const keys = (L && L.contenders) || [], xs = keys.map((k) => (P.get(k) ? P.get(k).kp : 0)).sort((a, b) => a - b);
  if (!xs.length) return null;
  const mid = xs.length % 2 ? xs[xs.length >> 1] : (xs[xs.length / 2 - 1] + xs[xs.length / 2]) / 2;
  return { keys: new Set(keys), kp: Math.round(STANDOUT_LINE * mid) };
}
/** Is a fighter in on the standout's way (a Contender whose Contender points clear the standout line)? */
/** A cup's crown value, least → most: Bottom vs Bottom / the Losers' Cup 1 · the Contenders Cup 2 · Last Chance 3 · Top vs Top 4 · the Circuit Cup 5 · Salvation 6 · the Night Crown 7 · the Season Finale 8. */
export const crownTier = (c) => (CUPS[c] && CUPS[c].tier) || ({ l: 1, k: 2, w: 4, x: 6, f: 8 })[c] || 1;
/** Is a fighter crowned this week (a cup of tier ≥ CROWN_ENTRY_TIER) — in Salvation on merit? */
export const isCrowned = (P, k) => !!(P.get(k) && P.get(k).crowns > 0);
export const isStandout = (P, so, k) => !!(so && so.keys.has(k) && P.get(k) && P.get(k).kp >= so.kp && so.kp > 0);
export function salvationField(P, ok = () => true, line = Infinity, so = null, extra = []) {
  const all = [...P.entries()].filter(([k]) => ok(k)).sort((x, y) => y[1].score - x[1].score || castOrder(x[0]) - castOrder(y[0]) || (x[0] < y[0] ? -1 : 1));
  const over = all.filter(([k, v]) => v.score >= line || isStandout(P, so, k) || v.crowns > 0);
  const pick = over.length >= SALVATION_MIN ? over : all.slice(0, SALVATION_MIN), cap = CUPS.s.size;
  // a crown is a ticket: the crowned keep their seats when more than 16 clear the line (the rest fill by score; order stays by score)
  const crowned = pick.filter(([, v]) => v.crowns > 0).slice(0, cap), rest = pick.filter(([, v]) => !(v.crowns > 0)).slice(0, cap - crowned.length), keep = new Set([...crowned, ...rest].map(([k]) => k));
  const base = pick.filter(([k]) => keep.has(k)).map(([k]) => k);
  // EMPTY SEATS (caP'n 2026-10-23: Qualifier winners get Salvation seats only "if it helps fill seats and it makes sense"): when fewer than 16 clear the line, the day's best Qualifier runs fill what's left — never ahead of anyone on merit
  for (const k of extra) if (base.length < cap && !base.includes(k) && ok(k)) base.push(k);
  return base;
}

// ════════════════════════════════════════════════════════════
//  APOCALYPSE — the shift change: four series, best of three
// ════════════════════════════════════════════════════════════
/**
 * Friday's series, from this week's rows. The table is FROZEN at the first Apocalypse hour (5pm; V2 8pm) —
 * as that hour's card knew it (isKnown), so every game's card draws the same series;
 * the drop zone is the Circuit's bottom 4 — before V2 the Losers' Cup winner excused (the next one up drops in;
 * V2's Losers' Cup is bragging rights only, caP'n 2026-10-21) — against the Contenders' top 4: the very bottom meets the best.
 * Resolves { drop:[4], rise:[4], series:[{ i, a, b, wins:[a,b], done, winner }], safe, hours }.
 */
export function apocalypsePlan(league, weekRows, week) {
  const hours = kindHours(week, "apocalypse");
  const strike = hours[0] ?? Infinity;
  const rows = cleanRows(weekRows);
  const frozen = standings(knownRows(rows.filter((r) => r.h < strike), strike));
  const lc = hourStart(strike) < V2_MS ? rows.find((r) => r.kind === "cup" && r.c === "l" && r.r === 2 && r.h < strike) : null;
  const safe = lc ? (lc.w === 0 ? lc.a : lc.b) : null;
  let drop, rise;
  if (Number.isFinite(strike) && hourInfo(strike).v3) {
    // V3: the League as Friday has it (the week's escapes in), each division on its own table; this week's escapees are safe
    const L = leagueOn(league, rows, hourInfo(strike).day), immune = new Set(L.escaped);
    drop = rankKeys(L.circuit, frozen, REC.circuit).filter((k) => !immune.has(k)).slice(-DROP).reverse();
    rise = rankKeys(L.contenders, frozen, REC.contenders).slice(0, DROP);
  } else {
    drop = rankKeys(league.circuit, frozen).filter((k) => k !== safe).slice(-DROP).reverse();   // the very bottom first
    rise = rankKeys(league.contenders, frozen).slice(0, DROP);                                  // the best first
  }
  const series = drop.map((a, i) => {
    const b = rise[i], wins = [0, 0];
    for (const r of rows) if (r.kind === "apo" && r.i === i && r.a === a && r.b === b) wins[r.w]++;
    const done = wins[0] >= SERIES_WINS || wins[1] >= SERIES_WINS;
    return { i, a, b, wins, done, winner: done ? (wins[0] >= SERIES_WINS ? a : b) : null };
  }).filter((s) => s.a && s.b);
  return { drop, rise, series, safe, hours };
}
/** Hour h's Apocalypse games: game g = the hour's index; a game is only fought if its series is still open. */
function apocalypseFeatures(h, info, { league, weekRows }) {
  const plan = apocalypsePlan(league, weekRows.filter((r) => r.h < h), info.week);
  const g = plan.hours.indexOf(h);
  if (g < 0) return [];
  return plan.series.filter((s) => !s.done && s.wins[0] + s.wins[1] === g).map((s) => ({ a: s.a, b: s.b, k: "apo", i: s.i, g }));
}
/**
 * A week's leagues, from last week's leagues and last week's rows: every Contender who won their
 * series takes the seat of the Circuit member they beat (0–4 swaps). An unfinished series: nobody moves.
 */
export function nextLeague(league, weekRows, week) {
  const plan = apocalypsePlan(league, weekRows, week);
  const up = plan.series.filter((s) => s.winner === s.b);
  const promoted = up.map((s) => s.b), relegated = up.map((s) => s.a);
  // V3: the week's nightly escapes first (the League as Saturday has it), then Apocalypse's swaps on top
  const sat = saturdayOf(week), L = dayIsV3(sat) ? leagueOn(league, weekRows, sat) : league;
  const circuit = L.circuit.filter((k) => !relegated.includes(k)).concat(promoted);
  const contenders = L.contenders.filter((k) => !promoted.includes(k)).concat(relegated);
  const esc = L.escaped && L.escaped.length ? { escaped: L.escaped.slice(), dropped: L.dropped.slice() } : {};
  return { circuit, contenders, promoted, relegated, ...esc };
}

// ════════════════════════════════════════════════════════════
//  THE CARD — who fights in hour h, in order
// ════════════════════════════════════════════════════════════
/**
 * The table pairs itself (Swiss): from the top down, each takes the nearest name below them that
 * neither has met in their last REMATCH_GAP bouts (any kind). Pairs come back top pair first.
 */
function swiss(keys, S, rows, rec = REC.week) {
  const recent = new Map();
  for (const r of rows) for (const [k, o] of [[r.a, r.b], [r.b, r.a]]) { const l = recent.get(k) || []; l.push(o); if (l.length > REMATCH_GAP) l.shift(); recent.set(k, l); }
  const met = (a, b) => (recent.get(a) || []).includes(b) || (recent.get(b) || []).includes(a);
  const left = rankKeys(keys, S, rec), pairs = [];
  while (left.length >= 2) {
    const a = left.shift();
    let j = left.findIndex((b) => !met(a, b));
    if (j < 0) j = 0;   // everyone's a rematch: the nearest it is
    pairs.push([a, left.splice(j, 1)[0]]);
  }
  return pairs;
}
/**
 * Hour h's card. ctx = { league, weekRows, seasonRows, tail? } — only the rows hour h's card KNOWS are used
 * (isKnown: ORDER IS THE LAW, up to the :50 line). tail = fights of the hour before that hadn't ended yet
 * ([{a,b}], from the stage): only WHO fought counts (no rematch, fair order), never who won.
 * Resolves { info, card:[bout], note } — bout = { a, b, k:"lb"|"cup"|"apo", dv?, fx?, c?, r?, i?, g? }.
 * Features (cups, Apocalypse games, the Main Event) open the card; then the Circuit's bouts, then the Contenders' (filler).
 * Prime Time and the Main Event put the top of the Circuit's table first; other hours put whoever has
 * fought least this week first (so what gets scratched at the end of an hour evens out).
 */
export function planHour(h, ctx) {
  const info = hourInfo(h);
  if (info.v3) return planHourV3(h, info, ctx);
  const before = cleanRows(ctx.weekRows).filter((r) => r.h < h);
  const weekRows = before.filter((r) => isKnown(r, h));
  // the last hour's fights that hadn't ended at :50: who fought is known (rows that came in late + what the stage says is still to come)
  const tail = before.filter((r) => !isKnown(r, h)).map((r) => ({ a: r.a, b: r.b })).concat(ctx.tail || []);
  const seasonRows = ctx.seasonRows ? cleanRows(ctx.seasonRows).filter((r) => isKnown(r, h)) : null;
  const league = ctx.league, S = standings(weekRows, league.elo || null);
  const c2 = { league, weekRows, seasonRows, entered: ctx.entered || null };
  let features = [], note = "";
  // BEST-LAST (caP'n 2026-10-22: "we need the best fights last … #1 always fights last"), WEEK 1's plain hours too — see LEGACY_BESTLAST_MS.
  // Only the NON-tournament fights (league hours, Prime Time, the Main Event); a cup's or Apocalypse's bouts run as they always did.
  const bestLast = !!info.counts && !info.v2 && (info.kind === "league" || info.kind === "prime" || info.kind === "main") && info.ms >= LEGACY_BESTLAST_MS;
  if (info.v2) {
    features = [...cupFeatures(h, info, c2), ...(info.kind === "apocalypse" ? apocalypseFeatures(h, info, c2) : [])];
    note = v2Note(info);
  } else if (info.kind === "losers" || info.kind === "winners" || info.kind === "finale") {
    features = cupFeatures(h, info, c2);
    note = info.kind === "losers" ? "The Circuit's bottom 8 — win it and you're safe from Friday's drop"
      : info.kind === "winners" ? "The Circuit's top 8 this week" : "The season's top 8 — win it and you're the champion";
  } else if (info.kind === "apocalypse") {
    features = apocalypseFeatures(h, info, c2);
    note = `The Circuit's bottom ${DROP} vs the Contenders' top ${DROP} — best of 3, the seat on the line`;
  } else if (info.kind === "main") {
    const [a, b] = rankKeys(league.circuit, S);
    if (a && b) features = [{ a, b, k: "lb", dv: "c", fx: "main" }];
    note = bestLast ? "The Main Event — the Circuit's #1 vs #2, the hour's last fight" : "The Main Event — the Circuit's #1 vs #2";
  } else if (info.kind === "prime") note = bestLast ? "Prime Time — the best fights last" : "Prime Time — the top of the Circuit first";
  else if (info.kind === "preseason") note = "Preseason — nothing counts until Season 1";
  const busy = new Set(features.flatMap((f) => [f.a, f.b]));
  // preseason (before genesis) nothing is recorded, so the table can't pair anyone: a seeded shuffle does
  const pre = (keys) => { const R = mulberry(strSeed(`pre:${h}`)), x = keys.slice(); for (let i = x.length - 1; i > 0; i--) { const j = Math.floor(R() * (i + 1)); [x[i], x[j]] = [x[j], x[i]]; } const out = []; for (let i = 0; i + 1 < x.length; i += 2) out.push([x[i], x[i + 1]]); return out; };
  // CHALLENGERS fight an hour's league bouts only when they said "I'm in" before its card went up (ctx.present);
  // an odd division sits out ONE NPC (the one who's fought most this week) — never a player who showed up
  const present = ctx.present || new Set();
  const here = (keys) => {
    const out = keys.filter((k) => !busy.has(k) && (!isChKey(k) || present.has(k)));
    if (out.length % 2 && keys.some(isChKey)) {   // (a division with a Challenger in it who isn't "in" goes odd too)
      const npcs = out.filter((k) => !isChKey(k)).sort((x, y) => statOf(S, y).played - statOf(S, x).played || castOrder(y) - castOrder(x));
      if (npcs.length) out.splice(out.indexOf(npcs[0]), 1);
    }
    return out;
  };
  const pairs = (keys, dv) => (info.counts ? swiss : pre)(here(keys), S, weekRows.concat(tail)).map(([a, b]) => ({ a, b, k: "lb", dv }));
  let C = pairs(league.circuit, "c"), K = pairs(league.contenders, "k");
  if (!info.prime) {
    const inTail = (k) => tail.reduce((n, t) => n + (t.a === k || t.b === k ? 1 : 0), 0);   // fought in the hour's last fights, winner unknown
    const fought = (b) => statOf(S, b.a).played + statOf(S, b.b).played + inTail(b.a) + inTail(b.b);
    const fair = (list) => list.map((b, n) => ({ b, n })).sort((x, y) => fought(x.b) - fought(y.b) || x.n - y.n).map((x) => x.b);
    C = fair(C); K = fair(K);
  }
  // BEST-LAST: the hour's biggest Circuit bout — the Main Event's #1 vs #2, else the table's best pair — becomes the CLOSER (cl): it rings
  // last, and the clock scratches fillers (never it) to make room (stage.js closersFit). The other Circuit bouts run weakest → strongest
  // by the table's rank sum, the Contenders' filler after them (the Circuit still comes first), so #1 fights last.
  let closer = null;
  if (bestLast) {
    const rk = rankKeys(league.circuit, S), at = (x) => { const i = rk.indexOf(x); return i < 0 ? rk.length : i; }, might = (b) => at(b.a) + at(b.b);
    const main = features.find((f) => f.fx === "main");
    if (main) { closer = { ...main, cl: 1 }; features = features.filter((f) => f !== main); }
    else if (C.length) { const best = C.reduce((m, b) => (might(b) < might(m) ? b : m), C[0]); closer = { ...best, cl: 1 }; C = C.filter((b) => b !== best); }
    C = C.map((b, n) => ({ b, n })).sort((x, y) => might(y.b) - might(x.b) || x.n - y.n).map((x) => x.b);   // weakest first (a tie: the order it had)
  }
  // THE CIRCUIT TAKES PRIORITY: all of its bouts, then the Contenders' — filler, so there's always a fight on
  const league2 = [...C, ...K];
  // nobody opens the hour who closed the last one (their bout just ended): the next bout of the SAME division steps up, so the Circuit stays first
  const last = tail.length ? tail[tail.length - 1] : weekRows[weekRows.length - 1];
  const justFought = (b) => !!last && (b.a === last.a || b.a === last.b || b.b === last.a || b.b === last.b);
  if (last && (tail.length || last.h === h - 1) && league2.length > 1 && justFought(league2[0])) {
    const i = league2.findIndex((b, n) => n > 0 && b.dv === league2[0].dv && !justFought(b));
    if (i > 0) league2.unshift(...league2.splice(i, 1));
  }
  // A PLAYER WHO SHOWED UP GETS THEIR FIGHT: a Challenger's league bout goes first after the features (they start at the
  // bottom of the Swiss, which would otherwise put them at the tail the :53 cut scratches), and never counts against the card's size
  const mine = league2.filter((b) => isChKey(b.a) || isChKey(b.b)), rest = league2.filter((b) => !mine.includes(b));
  const body = [...features, ...mine, ...rest].slice(0, CARD_MAX + mine.length - (closer ? 1 : 0));
  return { info, card: closer ? [...body, closer] : body, note };
}
// ════════════════════════════════════════════════════════════
//  THE CLIMAX LAW (caP'n 2026-10-22) — a stage's hour ends on its biggest fight
// ════════════════════════════════════════════════════════════
//  "make sure the best fights are now last, not the top of the hour, end on a climax so #1 shouldn't fight till the end instead of first"
//  A bout tagged cl closes its stage's hour (cup semifinals + finals, Apocalypse's games, else the table's best pair). The CLOCK
//  scratches fillers, never closers: a filler is only fought if, even with every fight going to the 5:00 bell, the closers still ring by
//  :53 — and a closer the NEXT hour's card needs (nx: a semifinal, an Apocalypse game 1–2) still ends by :50. Pure: stage.js, the keeper's
//  paper hours, the board's "about when" and the tests all ask the same function.
/** Which cup the climax puts last, least epic first (the same ladder as the night: caP'n "arrange them from least epic to most epic"). */
export const CLIMAX_LADDER = ["q1", "q2", "q3", "q4", "l", "bb", "kc", "lc", "apo", "tt", "c", "nc", "s", "f"];
/** Least epic first: a final after everything else, then the cup's rung, then the round; inside a round the strongest bout (the #1 seed) LAST. */
export function climaxCmp(x, y) {
  const fin = (b) => (b.k === "cup" && b.r === finalR(b.c) ? 1 : 0), rung = (b) => CLIMAX_LADDER.indexOf(b.k === "apo" ? "apo" : b.c);
  return fin(x) - fin(y) || rung(x) - rung(y) || (x.r || 0) - (y.r || 0) || (y.sd || 0) - (x.sd || 0) || (y.i || 0) - (x.i || 0) || (x.g || 0) - (y.g || 0);
}
export const isCloser = (b) => !!b && b.cl === 1;
/**
 * Would the stage's closers still make it if card bout j rings at `bell` (ms)? dur(b) = how long a fight is assumed to take (s):
 * callOf (the worst case, every fight to the bell) by default. Nothing after j on its stage is a closer → true.
 */
export function closersFit(card, j, bell, h, dur = callOf) {
  const ln = laneOf(card[j]);
  let t = bell + dur(card[j]) * 1000;
  for (let x = j + 1; x < card.length; x++) {
    const b = card[x];
    if (!isCloser(b) || laneOf(b) !== ln) continue;
    t += ringOf(card, x) * 1000;
    if (t > lastBellOf(h)) return false;
    t += dur(b) * 1000;
    if (b.nx && t > lockAtOf(h + 1)) return false;
  }
  return true;
}
/** Does a stage still have a closer after bout j? (only then does a filler have to make room for it) */
export const closerAfter = (card, j) => { const ln = laneOf(card[j]); for (let x = j + 1; x < card.length; x++) if (isCloser(card[x]) && laneOf(card[x]) === ln) return true; return false; };
/**
 * A paper hour (keeper.js): which of a stage's bouts are fought, and in what place (n → paperBell). Each stage fights PAPER_BOUTS — its
 * closers always, then as many fillers (in card order) as are left. -1 = not fought. A card with no closers (before V3): exactly the
 * stage's first PAPER_BOUTS, as it always was.
 */
export function paperAt(card, j) {
  const ln = laneOf(card[j]);
  let closers = 0;
  for (const b of card) if (isCloser(b) && laneOf(b) === ln) closers++;
  const cap = Math.max(0, PAPER_BOUTS - closers);
  let n = 0, fill = 0;
  for (let x = 0; x < card.length; x++) {
    const b = card[x];
    if (laneOf(b) !== ln) continue;
    const fought = isCloser(b) || fill < cap;
    if (!isCloser(b) && fought) fill++;
    if (x === j) return fought ? n : -1;
    if (fought) n++;
  }
  return -1;
}

/**
 * Hour h's card from V3 on — TWO STAGES IN UNISON (caP'n 2026-10-21: "The Contenders Tournament like the Circuit should be
 * 24/7 … they run in unison"). Lane 0 = THE CIRCUIT STAGE (the Circuit's league bouts, called at 5:00); lane 1 = THE
 * CONTENDERS STAGE (theirs, called at CALL_K 4:00 with RING_K between: a fight at least every 5 minutes). Each stage runs, in
 * order: its cup / Apocalypse bouts (the slot table picks the stage), a Challenger's bout, then TWO rounds of its division's
 * Swiss — the second avoids the first's pairings and starts with whoever fought earliest, so a stage never runs dry before
 * :53 (the tail is scratched as always). Anyone in a cup bout this hour sits out BOTH stages' league bouts: nobody is ever in
 * two places at once. The card is lane 0's bouts, then lane 1's (bout ids stay bt_{h}_{j}).
 */
function planHourV3(h, info, ctx) {
  const before = cleanRows(ctx.weekRows).filter((r) => r.h < h);
  const weekRows = before.filter((r) => isKnown(r, h));
  const tail = before.filter((r) => !isKnown(r, h)).map((r) => ({ a: r.a, b: r.b, ln: r.ln || 0 })).concat(ctx.tail || []);
  const seasonRows = ctx.seasonRows ? cleanRows(ctx.seasonRows).filter((r) => isKnown(r, h)) : null;
  const S = standings(weekRows, ctx.league.elo || null);
  const L = leagueOn(ctx.league, weekRows, info.day);
  const c2 = { league: ctx.league, weekRows, seasonRows, entered: ctx.entered || null };
  // (Apocalypse's games close the Circuit stage's hour; game 1 and 2 feed the next hour's game, so they must END by :50 — nx)
  const apoHours = info.kind === "apocalypse" ? kindHours(info.week, "apocalypse") : [];
  const features = [...cupFeatures(h, info, c2), ...(info.kind === "apocalypse" ? apocalypseFeatures(h, info, c2).map((f) => ({ ...f, ln: 0, cl: 1, ...(apoHours.indexOf(h) < apoHours.length - 1 ? { nx: 1 } : {}) })) : [])];
  const busy = new Set(features.flatMap((f) => [f.a, f.b]));
  const present = ctx.present || new Set();
  const here = (keys) => {   // (as before) a Challenger only when they're "in"; an odd division sits out the NPC who's fought most
    const out = keys.filter((k) => !busy.has(k) && (!isChKey(k) || present.has(k)));
    if (out.length % 2) {
      const npcs = out.filter((k) => !isChKey(k)).sort((x, y) => statOf(S, y).played - statOf(S, x).played || castOrder(y) - castOrder(x));
      if (npcs.length) out.splice(out.indexOf(npcs[0]), 1);
    }
    return out;
  };
  const recent = weekRows.concat(tail);
  const inTail = (k) => tail.reduce((n, t) => n + (t.a === k || t.b === k ? 1 : 0), 0);
  const fought = (b) => statOf(S, b.a).played + statOf(S, b.b).played + inTail(b.a) + inTail(b.b);
  const fair = (list) => list.map((b, n) => ({ b, n })).sort((x, y) => fought(x.b) - fought(y.b) || x.n - y.n).map((x) => x.b);
  const stage = (keys, dv, ln) => {
    const ks = here(keys), rec = divRec(dv);
    let r1 = swiss(ks, S, recent, rec).map(([a, b]) => ({ a, b, k: "lb", dv, ln }));
    if (!info.prime) r1 = fair(r1);
    const pos = new Map();
    r1.forEach((b, n) => { pos.set(b.a, n); pos.set(b.b, n); });
    // round two: never the same pair as round one (review 2026-10-22 — swiss's "nearest name" fallback could repeat it) — such a pair is dropped
    const met1 = new Set(r1.map((b) => [b.a, b.b].sort().join("|")));
    const r2 = swiss(ks, S, recent.concat(r1), rec).filter(([a, b]) => !met1.has([a, b].sort().join("|"))).map(([a, b]) => ({ a, b, k: "lb", dv, ln }))
      .map((b, n) => ({ b, n, at: Math.max(pos.get(b.a) ?? -1, pos.get(b.b) ?? -1) })).sort((x, y) => x.at - y.at || x.n - y.n).map((x) => x.b);
    const list = [...r1, ...r2];
    // nobody opens a stage's hour who closed it last hour: the next bout steps up
    const lastT = [...tail].reverse().find((t) => (t.ln || 0) === ln);
    const lastR = lastT || [...weekRows].reverse().find((r) => r.h === h - 1 && (r.ln || 0) === ln);
    const justFought = (b) => !!lastR && (b.a === lastR.a || b.a === lastR.b || b.b === lastR.a || b.b === lastR.b);
    if (lastR && list.length > 1 && justFought(list[0])) { const i = list.findIndex((b, n) => n > 0 && !justFought(b)); if (i > 0) list.unshift(...list.splice(i, 1)); }
    // THE CLIMAX LAW (caP'n 2026-10-22: "the best fights are last, not the top of the hour … #1 shouldn't fight till the end instead
    // of first"): a stage's hour ENDS on its biggest fight. A cup's semifinals and final (cl) and Apocalypse's games close the hour,
    // least epic first, the final last, and inside a round the cup's #1 seed fights last; with no such bout the stage ends on its
    // table's best pair. The earlier cup rounds still open the hour (THE :50 LAW needs them done early). Everything in between is the
    // filler it always was — and the fillers are what the clock scratches (stage.js closersFit), never the closers.
    const feat = features.filter((f) => (f.ln || 0) === ln).sort(climaxCmp);
    const early = feat.filter((f) => !f.cl), cups = feat.filter((f) => f.cl);
    let closer = null;
    if (!cups.length && list.length) {
      const order = rankKeys(ks, S, rec), at = (x) => { const i = order.indexOf(x); return i < 0 ? 99 : i; };
      closer = list.reduce((best, b) => (at(b.a) + at(b.b) < at(best.a) + at(best.b) ? b : best), list[0]);   // (a tie: round one's)
    }
    const pool = closer ? list.filter((b) => b !== closer) : list, r1p = closer ? r1.filter((b) => b !== closer) : r1;
    // a player who showed up gets their fight: their ROUND-ONE bout first after the stage's cup bouts; a round-two bout of theirs
    // stays at least CH_GAP bouts later (review 2026-10-22: both used to jump the queue — two fights back to back, 60 s apart)
    const ch = (b) => isChKey(b.a) || isChKey(b.b);
    const mine = r1p.filter(ch), rest = pool.filter((b) => !mine.includes(b));
    // THE BEST-LAST LAW, FILLERS TOO (caP'n 2026-10-22: "the toughest fights to be last … the other fights that aren't tournaments, let's make
    // sure the best always fight later, with #1 being last"): the bouts the clock will actually air (AIR_BOUTS — the rest are the scratchable
    // tail) go weakest pair first, strongest last (the table's rank sum, as the closer is picked), and the hour's best pair closes it, so #1
    // fights last. WHO gets aired is still the fair order (round one before round two, fewest-fought first) — only the running order changes.
    const rk = rankKeys(ks, S, rec), rkAt = (x) => { const i = rk.indexOf(x); return i < 0 ? rk.length : i; }, might = (b) => rkAt(b.a) + rkAt(b.b);
    const weakFirst = (xs) => xs.map((b, n) => ({ b, n })).sort((x, y) => might(y.b) - might(x.b) || x.n - y.n).map((x) => x.b);
    const air = Math.max(1, AIR_BOUTS - early.length - mine.length - (closer || cups.length ? 1 : 0));
    // the airable set: everyone's round-one bout, then the strongest round-two bouts until the slots run out (the weak round-two bouts
    // are the scratchable overflow) — and each half runs weakest → strongest
    const inR1 = (b) => r1.includes(b), twos = rest.filter((b) => !inR1(b)).sort((x, y) => might(x) - might(y));
    const win = rest.filter(inR1).concat(twos).slice(0, air), over = twos.filter((b) => !win.includes(b));
    // weakest first, but nobody fights twice running (the stage's previous hour counts): each slot takes the weakest bout left that
    // doesn't share a fighter with the one before. The closer's two fighters' other bouts are the strongest left, so they fall to the
    // back on their own — they don't fight early, and the hour still ends on the best pair.
    const shares = (p, q) => !!p && !!q && (p.a === q.a || p.a === q.b || p.b === q.a || p.b === q.b);
    const sequence = (xs, prev) => {
      const left = xs.slice(), out = [];
      let p = prev;
      while (left.length) { let x = left.findIndex((b) => !shares(p, b)); if (x < 0) x = 0; p = left.splice(x, 1)[0]; out.push(p); }
      return out;
    };
    rest.length = 0; rest.push(...sequence(weakFirst(win), lastR));
    for (const b of rest.filter(ch)) {   // (`mine` sits just before `rest`, so position CH_GAP in rest = CH_GAP bouts after it)
      const at = rest.indexOf(b);
      if (at < CH_GAP) { rest.splice(at, 1); rest.splice(Math.min(CH_GAP, rest.length), 0, b); }
    }
    rest.push(...sequence(weakFirst(over), rest[rest.length - 1] || mine[mine.length - 1] || lastR));   // (the scratchable overflow)
    const fill = [...mine, ...rest].slice(0, CARD_MAX + mine.length);
    const fin = closer || (cups.length ? cups[0] : null);
    // the greedy pass can strand two bouts of one being at the very back (the strongest left all share fighters): untangle what's left by
    // moving the earlier bout a little way BACK (or the later one forward) to the nearest slot where both neighbours are clear — and the
    // closer counts as the last neighbour (nobody fights twice in a row on the biggest bout of the hour)
    // (rare: the greedy pass can strand two bouts of one being side by side, or end on a bout that shares a fighter with the closer.) Search for the
    // arrangement closest to this one — earliest-first at every slot — with no one twice running; a small budget, and if none exists it stays as is.
    if (fill.some((b, n) => shares(n ? fill[n - 1] : lastR, b)) || shares(fill[fill.length - 1], fin)) {
      let budget = 20000;
      const dfs = (left, prev, out) => {
        if (!left.length) return shares(prev, fin) ? null : out;
        if (--budget < 0) return null;
        for (let i = 0; i < left.length; i++) {
          if (shares(prev, left[i])) continue;
          const r = dfs(left.filter((_, n) => n !== i), left[i], out.concat([left[i]]));
          if (r) return r;
        }
        return null;
      };
      const fixed = dfs(fill, lastR, []);
      if (fixed) { fill.length = 0; fill.push(...fixed); }
      else if (fin && fill.length > 1 && shares(fill[fill.length - 1], fin)) {   // no clean order exists (a tiny hour): at least not into the closer
        for (let x = fill.length - 2; x >= 0; x--) if (!shares(fill[x], fin)) { fill.push(...fill.splice(x, 1)); break; }
      }
    }
    return [...early, ...fill, ...(closer ? [{ ...closer, cl: 1 }] : cups)];
  };

  const card = [...stage(L.circuit, "c", 0), ...stage(L.contenders, "k", 1)];
  return { info, card, note: v3Note(info), league: L };
}
/** The one line under an hour's card (V3). */
function v3Note(info) {
  const hr = info.localHour, k = info.kind;
  const fin = { bbcup: "Bottom vs Bottom", kccup: "the Contenders Cup", lccup: "Last Chance — one Contender escapes", ttcup: "Top vs Top — one Contender escapes", ccup: "the Circuit Cup" };
  if (k === "rounds") return hr === 13 ? "The cups begin — Bottom vs Bottom and Top vs Top" : hr === 14 ? "The Contenders Cup and the Circuit Cup begin" : hr === 15 ? "Last Chance begins, on both stages" : "The cup rounds";
  if (k === "qual") return hr === 11 ? "The Qualifiers — 8 fighters a tourney, 7 fights back to back — the semifinalists make the top half of tonight's cups" : "The Qualifiers, round two — the cups are drawn from these results at 1pm";
  if (k === "ccup" && hr === 22 && info.dow <= 4) return "Prime Time — the Circuit Cup final, then the Night Crown";
  if (fin[k]) return `Prime Time — the final of ${fin[k]}`;
  if (k === "apocalypse") return `Apocalypse — the Circuit's bottom ${DROP} vs the Contenders' top ${DROP}, best of 3, the seat on the line`;
  if (k === "losers") return "The Losers' Cup — bragging rights — and Salvation begins";
  if (k === "salvation") return hr === 20 ? "Salvation — the King's Cup — then the Losers' Cup final" : hr === 21 ? "Salvation semifinals" : "Salvation — the King's Cup final";
  if (k === "finale") return hr === 20 ? "The Season Finale — the season's top 8 — then the Losers' Cup final" : hr === 21 ? "The Season Finale semifinals" : "The Season Finale — win it and you're the champion";
  return info.prime ? "Prime Time — the best fights last" : "";
}
/** The one line under an hour's card (V2). */
function v2Note(info) {
  const hr = info.localHour, k = info.kind;
  if (k === "ccup") return hr === 18 ? "Prime Time — the Circuit Cup and the Contenders Cup begin"
    : hr === 19 ? "Prime Time — the Circuit Cup's round of 16"
      : hr === 20 ? "The Circuit Cup's quarters, then the Contenders Cup semis"
        : hr === 21 ? "The Circuit Cup semifinals, then the Contenders Cup final" : "The Circuit Cup final";
  if (k === "apocalypse") return `Apocalypse — the Circuit's bottom ${DROP} vs the Contenders' top ${DROP}, best of 3, the seat on the line`;
  if (k === "losers") return "The Losers' Cup — bragging rights, open to every player not in the Champions Cup";
  if (k === "champions") return hr === 20 ? "The Champions Cup — the week's champions — then the Losers' Cup final" : hr === 21 ? "The Champions Cup semifinals" : "The Champions Cup final";
  if (k === "finale") return hr === 20 ? "The Season Finale — the season's top 8 — then the Losers' Cup final" : hr === 21 ? "The Season Finale semifinals" : "The Season Finale — win it and you're the champion";
  return info.prime ? "Prime Time — the top of the Circuit first" : "";
}
/** How long the ringside before bout j of a card is (s). */
export const ringOf = (card, j) => {
  const b = card[j], ln = laneOf(b);
  let prev = null;
  for (let x = j - 1; x >= 0; x--) if (laneOf(card[x]) === ln) { prev = card[x]; break; }
  const first = !prev;   // the first bout of ITS stage this hour (V3 has two)
  if (first) return Math.max(RING_OPEN, isBig(b) ? RING_BIG : 0);
  const own = isBig(b) ? RING_BIG : b && b.k === "lb" && ln === 1 ? RING_K : RING;
  // THE BREATHER (caP'n 2026-10-23: "after a tournament ends maybe 3-5 minutes down time"): the stage's first fight after a tournament's final waits BREATHER_S
  return prev.k === "cup" && prev.r === finalR(prev.c) ? Math.max(own, BREATHER_S) : own;
};
/** Which stage a bout is on (0 = the Circuit stage; everything before V3 is on it). */
export const laneOf = (b) => (b && b.ln === 1 ? 1 : 0);
/** When a bout is called (s): a Contenders-stage league bout at CALL_K (4:00), everything else at the game's 5:00. */
export const callOf = (b) => (b && b.k === "lb" && laneOf(b) === 1 ? CALL_K : CALL);
/** Gets the long ringside: every feature, except a nightly cup's rounds before its final (V2: they get the usual 90 s —
 *  ten cups a week would otherwise eat fights, and caP'n's rule is never fewer fights). */
export const isBig = (b) => isFeature(b) && !(b.k === "cup" && CUPS[b.c] && CUPS[b.c].daily && b.r < finalR(b.c));
export const isFeature = (b) => !!b && (b.k !== "lb" || b.fx === "main");
export const lastBellOf = (h) => hourStart(h) + LAST_BELL_MIN * MIN;
/** The bell of a paper bout (nobody saw the clock, so a fixed rhythm — only order matters). */
export const paperBell = (h, j) => hourStart(h) + RING_OPEN * 1000 + j * 4.5 * MIN;
/** A bout's place on ITS stage (0, 1, 2…) — paper hours fight each stage's first PAPER_BOUTS, rung by this (V3 has two stages). */
export const laneIndex = (card, j) => { const ln = laneOf(card[j]); let n = 0; for (let x = 0; x < j; x++) if (laneOf(card[x]) === ln) n++; return n; };

// ════════════════════════════════════════════════════════════
//  THE CLOSER — the last Circuit bout of an hour gets a spotlight
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-17: "the last circuit match of the night deserves recognition and its Friday so it
//  should be pretty special … if it was an even more important match it should get even more
//  recognition". DISPLAY ONLY: nothing here decides who fights or when — a card reads the same with
//  or without it. Every hour's closer is ★. Each of these is a reason to go bigger, and two or more
//  go biggest (★★★):
//    · a special night (SPECIAL_NIGHTS — Friday)        · the racing day's last hour (9pm)
//    · something on the line (a top-3 name, the drop zone, the Winners' Cup bubble)
//    · BOTH sides have something on the line
/** The bout that closes a card: its LAST Circuit bout (a feature or the Contenders' filler never is).
 *  skip(b, j) rules a bout out (scratched, or too late to be sure of). -1 if none. */
export function closerIndex(card, skip = () => false) {
  // (BEST-LAST: when the Main Event closes the hour it IS the climax and has its own big treatment — no second spotlight on a lesser bout)
  const end = card[card.length - 1];
  if (end && end.fx === "main" && end.cl === 1) return -1;
  for (let j = card.length - 1; j >= 0; j--) {
    const b = card[j];
    if (b && b.k === "lb" && b.dv === "c" && b.fx !== "main" && !skip(b, j)) return j;
  }
  return -1;
}
export const ZONE_RANK = ["drop", "title", "bubble"];   // which reason is named when a bout has several
export const STAKE_WORDS = {
  drop: ["Fighting the drop", "Both fighting the drop"],
  title: ["A top-three name", "Two of the top three"],
  bubble: ["On the Winners' Cup bubble", "Both on the Winners' Cup bubble"],
};
/**
 * Who has something on the line in a Circuit bout fought in hour h, as the table stands: Map(key → zone).
 * "drop" the bottom DROP (the Losers' Cup winner excused) until Friday's table is frozen · "title" the top 3 ·
 * "bubble" the places around the Winners' Cup cut (7th–10th) until Saturday's table is frozen.
 * Nobody has anything on the line at 0–0 (a fresh week), in preseason, or in the Finale's week (it seeds by the season).
 */
export function zonesOf(league, S, h, safe = null) {
  const out = new Map(), info = hourInfo(h);
  if (!info.counts || !league || !league.circuit) return out;
  if (!league.circuit.some((k) => { const s = statOf(S, k); return s.lw + s.ll > 0; })) return out;
  const ranked = rankKeys(league.circuit, S, info.v3 ? REC.circuit : REC.week);
  if (info.v3) {   // V3: someone can fall every night (an escape), so the drop zone is always live; this week's escapees are safe
    const imm = new Set(league.escaped || []), drop = new Set(dropOrder(league.circuit, S, imm).slice(0, DROP));
    ranked.forEach((k, i) => { if (drop.has(k)) out.set(k, "drop"); else if (i < 3) out.set(k, "title"); });
    return out;
  }
  // V2 has no "Winners' Cup bubble": the Champions Cup seats the week's cup winners first, so there's no fixed line on the table
  const strike = kindHours(info.week, "apocalypse")[0], cut = info.v2 ? null : kindHours(info.week, "winners")[0];
  const drop = new Set(strike != null && h < strike ? ranked.filter((k) => k !== safe).slice(-DROP) : []);
  ranked.forEach((k, i) => {
    if (drop.has(k)) out.set(k, "drop");
    else if (i < 3) out.set(k, "title");
    else if (cut != null && h < cut && i >= CUP_SIZE - 2 && i <= CUP_SIZE + 1) out.set(k, "bubble");
  });
  return out;
}
/** What's on the line in a bout: { zone, both, text } or null (zones from zonesOf). */
export function stakesOf(b, zones) {
  if (!zones || !b) return null;
  const zs = [zones.get(b.a), zones.get(b.b)].filter(Boolean), zone = ZONE_RANK.find((z) => zs.includes(z));
  return zone ? { zone, both: zs.length === 2, text: STAKE_WORDS[zone][zs.filter((z) => z === zone).length === 2 ? 1 : 0] } : null;
}
/**
 * A closer's spotlight: { level 1–3, night, lastNight, stakes }. fought: it's over, so the table has
 * moved on — only the night itself counts (what was on the line then isn't known now).
 */
export function spotOf(b, info, zones = null, fought = false) {
  const night = (info.localHour >= (info.v2 ? PRIME_FROM : NIGHT_FROM) && SPECIAL_NIGHTS[info.dow]) || null;
  const lastNight = info.localHour === dayTurnOf(info.ms) - 1;
  const stakes = fought ? null : stakesOf(b, zones);
  const n = (night ? 1 : 0) + (lastNight ? 1 : 0) + (stakes ? 1 : 0) + (stakes && stakes.both ? 1 : 0);
  return { level: 1 + (n >= 1 ? 1 : 0) + (n >= 2 ? 1 : 0), night, lastNight, stakes };
}
/** "Last Circuit bout of the hour" / "…of the night" (the racing day's last hour). */
export const spotTitle = (sp) => `Last Circuit bout of the ${sp.lastNight ? "night" : "hour"}`;

// ════════════════════════════════════════════════════════════
//  WHAT'S LEFT — the clock alone says how much is still to come (pure)
// ════════════════════════════════════════════════════════════
const BIG_KINDS = ["main", "losers", "apocalypse", "winners", "finale", "kcup", "ccup", "champions", "rounds", "bbcup", "kccup", "lccup", "ttcup", "salvation"];
/**
 * A week's big nights in order: [{ kind, name, hours, c?, day? }] — before V2 one per kind (Thursday's cup,
 * Friday's Apocalypse, Saturday's cup or the Finale); V2 every cup of every night (each with its own hours,
 * so the Contenders Cup ends at 9pm and the Circuit Cup at 10pm though they share the hours) + Friday's Apocalypse.
 */
export function weekEvents(week) {
  const old = ["losers", "apocalypse", "winners", "finale"].map((kind) => ({ kind, name: KINDS[kind].name, hours: kindHours(week, kind).filter((h) => hourStart(h) < V2_MS) })).filter((e) => e.hours.length);
  const v2 = [];
  for (let d = week * 7 - 4; d < week * 7 + 3; d++) {
    for (const c of cupsOfDay(d)) v2.push({ kind: CUPS[c].kind, name: KINDS[CUPS[c].kind].name, hours: cupHours(c, d), c, day: d });
    const apo = hoursOfDay(d).filter((h) => hourInfo(h).v2 && hourInfo(h).kind === "apocalypse");
    if (apo.length) v2.push({ kind: "apocalypse", name: KINDS.apocalypse.name, hours: apo, day: d });
  }
  return [...old, ...v2].sort((x, y) => x.hours[0] - y.hours[0]);
}
/**
 * From the clock alone: { pre, startsIn, tonight:{ fights, next }, week:{ events, fights, mains }, season }.
 * fights = a GUESS (FIGHTS_PER_HOUR a whole hour still to come) — shown as "about"; the hour on air is counted from its card.
 * events = this week's big nights in order, each { kind, name, start, end, state: "done"|"on"|"next" }.
 */
export function agenda(now) {
  const h = hourOf(now), week = weekOf(now), day = racingDayOf(now), pre = now < GENESIS_MS;
  const ahead = (hs) => hs.filter((x) => x > h).length * FIGHTS_PER_HOUR;
  const ev = (kind, hs, name = KINDS[kind].name) => {
    const start = hourStart(hs[0]), end = hourStart(hs[hs.length - 1]) + HOUR_MS;
    return { kind, name, start, end, state: end <= now ? "done" : start <= now ? "on" : "next" };
  };
  const events = pre ? [] : weekEvents(week).map((e) => ev(e.kind, e.hours, e.name)).sort((x, y) => x.start - y.start);
  const tonight = hoursOfDay(day), headline = tonight.find((x) => x >= h && BIG_KINDS.includes(hourInfo(x).kind));
  const weekHours = hoursOfWeek(week), sn = Math.max(0, seasonOf(week));
  return {
    pre, startsIn: GENESIS_MS - now,
    tonight: { fights: ahead(tonight), next: headline == null ? null : { name: hourInfo(headline).name, at: hourStart(headline) } },
    week: { events, fights: ahead(weekHours), mains: weekHours.filter((x) => x >= h && hourInfo(x).kind === "main").length },
    season: pre ? null : { n: sn + 1, week: weekOfSeason(week), weeks: SEASON_WEEKS, finale: finaleAt(sn) },
  };
}

// ════════════════════════════════════════════════════════════
//  WILLIE'S LINE — the odds from form (a guess, never a promise)
// ════════════════════════════════════════════════════════════
/** A week's closing ratings, to store with next week's leagues: { key: elo }. */
export const closingRatings = (S, eloStart = null) => ({ ...(eloStart || {}), ...Object.fromEntries([...S.values()].map((s) => [s.key, Math.round(s.elo * 10) / 10])) });
/** { p:[pA,pB], fair:[payA,payB] } — fair = what a fair book would pay per 1 staked. */
export function willieLine(sA, sB) {
  const pA = eloP(sA.elo ?? ELO_START, sB.elo ?? ELO_START);
  return { p: [pA, 1 - pA], fair: [1 / pA, 1 / (1 - pA)] };
}
/** Head to head inside some rows: [winsA, winsB]. */
export function headToHead(rows, a, b) {
  const out = [0, 0];
  for (const r of rows) {
    if (r.w !== 0 && r.w !== 1) continue;
    if (r.a === a && r.b === b) out[r.w]++;
    else if (r.a === b && r.b === a) out[1 - r.w]++;
  }
  return out;
}

// ════════════════════════════════════════════════════════════
//  WORDS FOR A BOUT — "the Main Event" · "Winners' Cup semi" · "Apocalypse, game 2"
// ════════════════════════════════════════════════════════════
const ROUND = ["quarterfinal", "semifinal", "final"];
const ROUND16 = ["round of 16", "quarterfinal", "semifinal", "final"];
/** "round of 16" · "quarterfinal" · "semifinal" · "final" — by the cup's size. */
export const roundName = (c, r) => (roundsOf(c) === 4 ? ROUND16 : roundsOf(c) === 1 ? ["final"] : ROUND)[r] || "";
export function boutLabel(b) {
  if (!b) return "";
  if (b.kind === "cup" || b.k === "cup") return b.c === "f" && b.r === finalR("f") ? "the Season Finale" : `${CUPS[b.c] ? CUPS[b.c].name.replace(/^the /, "") : "Cup"} ${roundName(b.c, b.r)}`.trim();
  if (b.kind === "apo" || b.k === "apo") return `Apocalypse, game ${(b.g || 0) + 1}`;
  if (b.fx === "main") return "the Main Event";
  return b.dv === "k" ? "Contenders bout" : "Circuit bout";
}
export const rowLabel = boutLabel;

// ════════════════════════════════════════════════════════════
//  A BEING'S PAGE — read straight off the rows (pure, any game's ledger)
// ════════════════════════════════════════════════════════════
/**
 * key's story in some rows. S = standings over the same rows (for opponents' ratings).
 * Resolves { fights, rivals:[{ key, met, won, lost }], best:{ row, opp }|null, recent:[{ row, opp, won }] }.
 */
export function profileOf(key, rows, S) {
  const mine = cleanRows(rows).filter((r) => r.a === key || r.b === key);
  const opps = new Map();
  let best = null;
  const recent = [];
  for (const r of mine) {
    const side = r.a === key ? 0 : 1, opp = side === 0 ? r.b : r.a, won = r.w === side;
    const o = opps.get(opp) || { key: opp, met: 0, won: 0, lost: 0 };
    o.met++; o[won ? "won" : "lost"]++; opps.set(opp, o);
    if (won && (!best || statOf(S, opp).elo > statOf(S, best.opp).elo)) best = { row: r, opp };
    recent.push({ row: r, opp, won });
  }
  const rivals = [...opps.values()].sort((x, y) => y.met - x.met || Math.abs(x.won - x.lost) - Math.abs(y.won - y.lost) || (x.key < y.key ? -1 : 1)).slice(0, 3);
  return { fights: mine.length, rivals, best, recent: recent.slice(-8).reverse() };
}

// ════════════════════════════════════════════════════════════
//  THE HALL — a finished season, remembered (pure)
// ════════════════════════════════════════════════════════════
export const FASTEST_MIN = 5;   // ⟵ DIAL — timed wins needed to be in the running for Fastest Ruin
/**
 * A season's honours: { champion, top, contender, fastest, cups:[{ week, w, l }] }.
 * champion = the Finale's winner · top = the Circuit's best season record · contender = the Contenders'
 * best season record (each judged on its own division's bouts) · fastest = best average win ·
 * cups = each week's Winners' and Losers' Cup winners. Ties go to the earlier name on caP'n's lists.
 */
export function seasonAwards(rows, league) {
  const clean = cleanRows(rows), S = standings(clean);
  // V3: escapes move people mid-week — the honours read the League as the season ended (its last Saturday)
  if (league && clean.length) { const lw = weekOf(clean[clean.length - 1].at); league = leagueOn(league, clean.filter((r) => weekOf(r.at) === lw), saturdayOf(lw)); }
  const fin = clean.find((r) => r.kind === "cup" && r.c === "f" && r.r === finalR("f"));
  const net = (rec) => (s) => { const [w, l] = rec(s); return w - l; };
  const best = (keys, score, ok = () => true) => [...new Set(keys)].map((k) => statOf(S, k)).filter(ok)
    .sort((x, y) => score(y) - score(x) || castOrder(x.key) - castOrder(y.key))[0] || null;
  const top = best(league.circuit, net(REC.circuit), (s) => s.cw + s.cl > 0);
  const con = best(league.contenders, net(REC.contenders), (s) => s.kw + s.kl > 0);
  const fast = best([...S.keys()], (s) => -avgWin(s), (s) => s.timed >= FASTEST_MIN);
  const weeks = [...new Set(clean.map((r) => weekOf(r.at)))].sort((a, b) => a - b);
  const cups = weeks.map((w) => {
    const champ = (c) => { const r = clean.find((x) => x.kind === "cup" && x.c === c && x.r === finalR(c) && weekOf(x.at) === w); return r ? (r.w === 0 ? r.a : r.b) : null; };
    return { week: w, w: champ("s") || champ("x") || champ("w"), l: champ("l") };   // V2: the Champions Cup is the week's top cup · V3: Salvation
  }).filter((x) => x.w || x.l);
  return {
    champion: fin ? (fin.w === 0 ? fin.a : fin.b) : null,
    top: top && { key: top.key, value: [top.cw, top.cl] },
    contender: con && { key: con.key, value: [con.kw, con.kl] },
    fastest: fast && { key: fast.key, value: Math.round(avgWin(fast)) },
    cups,
  };
}
// ════════════════════════════════════════════════════════════
//  THE HALL OF FAME — every night and every week, remembered (pure; caP'n 2026-10-21: "a quick hall of fame that shows
//  winners of the hourlies, of the prime time matches, best fighters of the day, champs of the week etc")
// ════════════════════════════════════════════════════════════
export const DAY_MIN = 3;   // ⟵ DIAL — league bouts in a day to be in the running for Fighter of the Day
const winnerOf = (r) => (r.w === 0 ? r.a : r.b), loserOf = (r) => (r.w === 0 ? r.b : r.a);
/**
 * One racing day's honours from that day's rows: { day, cups:[{ c, name, champion, runnerUp, at }], fighter:{ key, w, l }|null,
 * prime:[{ key, w, l }] (the best of Prime Time — the evening's league bouts), closer:{ key, opp, at }|null (the night's last Circuit bout) }.
 */
export function dayHonours(rows, day) {
  const mine = cleanRows(rows).filter((r) => racingDayOf(hourStart(r.h)) === day);
  const cups = mine.filter((r) => r.kind === "cup" && !isQual(r.c) && r.r === finalR(r.c)).map((r) => ({ c: r.c, name: CUPS[r.c].name, champion: winnerOf(r), runnerUp: loserOf(r), at: r.at }));
  const tally = (list) => {
    const m = new Map();
    for (const r of list) for (const [k, won] of [[r.a, r.w === 0], [r.b, r.w === 1]]) { const x = m.get(k) || { key: k, w: 0, l: 0 }; x[won ? "w" : "l"]++; m.set(k, x); }
    return [...m.values()];
  };
  const best = (xs, min) => xs.filter((x) => x.w + x.l >= min).sort((a, b) => b.w / (b.w + b.l) - a.w / (a.w + a.l) || (b.w - b.l) - (a.w - a.l) || b.w - a.w || castOrder(a.key) - castOrder(b.key));
  const lb = mine.filter((r) => r.kind === "lb");
  const fighter = best(tally(lb), DAY_MIN)[0] || null;
  const prime = best(tally(lb.filter((r) => hourInfo(r.h).prime)), 2).slice(0, 3);
  const circ = lb.filter((r) => r.dv === "c"), last = circ[circ.length - 1];
  return { day, cups, fighter, prime, closer: last ? { key: winnerOf(last), opp: loserOf(last), at: last.at } : null };
}
/**
 * One week's honours: { week, cups:[…every final of the week], apocalypse:[{ key, opp, rose }], top:{ circuit, contenders }, challenger }.
 * top = the week's table leaders (qualified) · challenger = the best Challenger (a player or partner) on win %, qualified.
 */
export function weekHonours(rows, league, week) {
  const clean = cleanRows(rows).filter((r) => weekOf(r.at) === week), S = standings(clean, league && league.elo);
  const cups = clean.filter((r) => r.kind === "cup" && !isQual(r.c) && r.r === finalR(r.c)).map((r) => ({ c: r.c, name: CUPS[r.c].name, champion: winnerOf(r), runnerUp: loserOf(r), at: r.at, day: racingDayOf(hourStart(r.h)) }));
  const ap = league ? apocalypsePlan(league, clean, week).series.filter((x) => x.done).map((x) => ({ key: x.winner, opp: x.winner === x.a ? x.b : x.a, rose: x.winner === x.b })) : [];
  const lead = (keys) => { const k = rankKeys(keys || [], S)[0]; if (!k) return null; const s = statOf(S, k); return s.lw + s.ll >= QUALIFY ? { key: k, w: s.lw, l: s.ll } : null; };
  const chs = [...new Set(clean.flatMap((r) => [r.a, r.b]).filter(isChKey))].map((k) => { const s = statOf(S, k); return { key: k, w: s.lw, l: s.ll }; }).filter((x) => x.w + x.l >= QUALIFY)
    .sort((a, b) => b.w / (b.w + b.l) - a.w / (a.w + a.l) || b.w - a.w || (a.key < b.key ? -1 : 1));
  return { week, cups, apocalypse: ap, top: { circuit: league ? lead(league.circuit) : null, contenders: league ? lead(league.contenders) : null }, challenger: chs[0] || null };
}
/** A key's medals from some rows: { k, c, x, l, w, f } cups won (a Challenger's shelf, or anyone's). */
export function medalsOf(key, rows) {
  const out = { k: 0, c: 0, x: 0, l: 0, w: 0, f: 0, bb: 0, kc: 0, lc: 0, tt: 0, s: 0, nc: 0 };
  for (const r of cleanRows(rows)) if (r.kind === "cup" && r.r === finalR(r.c) && winnerOf(r) === key) out[r.c] = (out[r.c] || 0) + 1;
  return out;
}
/**
 * A fighter's CROWNS from some rows (caP'n 2026-10-22: "a badge … only tourney winners get"): one for every cup final won.
 * Resolves Map(key → { n: how many, tier: the biggest one's crownTier, cups: { cupKey: count } }). Pure — the week's rows for the week's crowns,
 * the season's for the season's. (A bye never crowns: only a fought final leaves a row.)
 */
/** Who has WON a Qualifier (its final) in these rows: Map(key → how many). Qualifiers leave no crown, just the Q (ui/Crown.jsx QMark). */
export function qualWinsOf(rows) {
  const out = new Map();
  for (const r of cleanRows(rows || [])) if (r.kind === "cup" && isQual(r.c) && r.r === finalR(r.c)) { const k = winnerOf(r); out.set(k, (out.get(k) || 0) + 1); }
  return out;
}
export function crownsOf(rows) {
  const out = new Map(), all = cleanRows(rows || []);
  for (const r of all) {
    if (r.kind !== "cup" || !CUPS[r.c] || isQual(r.c) || r.r !== finalR(r.c)) continue;   // (a Qualifier leaves no crown — it's the way IN)
    const k = winnerOf(r), x = out.get(k) || { n: 0, tier: 0, cups: {}, night: { lord: 0, champ: 0 } };
    x.n++; x.tier = Math.max(x.tier, crownTier(r.c)); x.cups[r.c] = (x.cups[r.c] || 0) + 1;
    if (r.c === "nc") x.night[nightTitleOf(all, r)]++;
    out.set(k, x);
  }
  // ONE title at a time (caP'n 2026-10-23: only one Night Lord / Night's Champion, or it stops feeling like they "won the night"): the title
  // belongs to the LATEST Night Crown's winner alone. Earlier nights' winners keep their crowns (and the count), just not the title word.
  let last = null;
  for (const r of all) if (r.kind === "cup" && r.c === "nc" && r.r === finalR("nc") && (!last || r.h > last.h)) last = r;
  const reign = last ? winnerOf(last) : null;
  for (const [k, x] of out) x.night = k === reign ? { lord: nightTitleOf(all, last) === "lord" ? 1 : 0, champ: nightTitleOf(all, last) === "champ" ? 1 : 0 } : { lord: 0, champ: 0 };
  return out;
}
/**
 * THE NIGHT'S TITLES (caP'n 2026-10-22): the Night Crown's winner is the NIGHT LORD when the Circuit is in the fight (either side — "if both are
 * participating the winner is Night Lord"), the NIGHT'S CHAMPION when both fighters are Contenders. "lord" | "champ" for a Night Crown row `r`.
 * A fighter's division that night = the division of their league bouts that day (they fight in one per day); unknown → the bigger title.
 */
export function nightTitleOf(rows, r) {
  const day = racingDayOf(hourStart(r.h)), dv = (k) => { const lb = rows.find((x) => x.kind === "lb" && (x.a === k || x.b === k) && x.dv && racingDayOf(hourStart(x.h)) === day); return lb ? lb.dv : null; };
  return dv(r.a) === "k" && dv(r.b) === "k" ? "champ" : "lord";
}
/** When a season's Finale final rings — its hour (ms). */
export const finaleAt = (s) => hourStart(kindHours(seasonWeeks(s)[SEASON_WEEKS - 1], "finale").slice(-1)[0]);
